// src/features/data/groups-unified-tab.tsx
import {
  AvatarStopLines,
  Chip,
  CollectionAxisChip,
  DataTable,
  GroupStatusCell,
  Kicker,
  LcpTotals,
  LifecycleChip,
  LifecycleReason,
  StatusDot,
} from "@/components/iris";
import type { DataTableColumn, SortDirection, StatusDotVariant } from "@/components/iris";
import {
  DateWindowFilter,
  type DateWindowValue,
  EMPTY_DATE_WINDOW,
} from "@/components/ui/date-window-filter";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { MultiSelectFilter } from "@/components/ui/multi-select-filter";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { BulkBar } from "@/features/entities/bulk-bar";
import { LastActiveDisplay } from "@/features/entities/last-active-display";
// IR-1147 reopen: reuse the Join Links tab building blocks (Rotem: "like in
// join link tab") — the searchable multi-select for the Avatar filter and the
// platform brand glyphs for the Platform column.
import { MultiSelectFilter as SearchableMultiSelectFilter } from "@/features/links/MultiSelectFilter";
import { PlatformIcon, platformToIconCode } from "@/features/links/PlatformIcon";
import {
  useFleetAvatars,
  useGroupLifecycleAction,
  useSetGroupNotes,
  useTerminateCoverage,
  useUnifiedGroups,
} from "@/hooks/use-fleet";
import {
  fmtCreatedDate,
  fmtLocalDateTime,
  fmtLocalDateTimeWithZone,
  isNewGroup,
  viewerZoneLabel,
} from "@/lib/group-created";
import {
  CAXIS_KEYS,
  DEFAULT_LCP_FILTER,
  LCP_KEYS,
  caxisOptions,
  lcpOptions,
  lcpSliceHexForItem,
  windowCaption,
} from "@/lib/lifecycle-matrix";
import type { MessagesQualityAvatar, UnifiedGroupRecord } from "@/lib/types";
import { ChevronDown, ChevronUp, Info, MoreHorizontal, Search } from "lucide-react";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { ActiveDeleteJobsBanner, type JobEntry } from "./active-delete-jobs-banner";
import { AvatarsCell } from "./avatars-cell";
import { ConnectedDonut, STICKY_ACTIONS_CLASS } from "./avatars-tab";
import { GroupCard } from "./group-card";
import { GroupDeleteDialog } from "./group-delete-dialog";
import { GroupEditDialog } from "./group-edit-dialog";
import {
  loadStatsCollapsed,
  lockHeightFor,
  saveStatsCollapsed,
  scrollPageToTop,
  shouldHideStatsForScroll,
} from "./groups-stats-strip";
import {
  ENV_INGESTION_FILTER_OPTIONS,
  INGESTION_COL_MIN_WIDTH,
  MESSAGES_QUALITY_FILTER_OPTIONS,
  ON_PHONE_FILTER_OPTIONS,
  addedFilterParams,
  envIngestionRing,
  envIngestionSliceColor,
  fmtAvatarPhone,
  formatSummaryNumber,
  supportsMessagesQualityFilter,
  supportsOnPhoneFilter,
} from "./groups-unified-tab.helpers";
import { IngestionPairCell, PipelineStatusCell } from "./ingestion-pair-cell";

// ─── Helpers ───

function statusVariant(status: string | null) {
  switch (status) {
    case "active":
      return "ok" as const;
    case "inactive":
      return "inactive" as const;
    case "deleted":
      return "err" as const;
    default:
      return "idle" as const;
  }
}

// IR-1227 (Rotem 53724): group collection lifecycle badge. 'collecting' →
// green dot; 'stopped' → "Collection stopped" (muted, the attention state);
// null → em-dash (never collected).
const COLLECTION_STATE_LABELS: Record<string, string> = {
  collecting: "Collecting",
  stopped: "Collection stopped",
};
export function collectionStateLabel(s: string | null | undefined): string {
  return s ? (COLLECTION_STATE_LABELS[s] ?? s) : "—";
}
export function collectionStateVariant(s: string | null | undefined) {
  if (s === "collecting") return "ok" as const;
  if (s === "stopped") return "inactive" as const;
  return "idle" as const;
}

// IR-1227 (Rotem 53901): derived single group lifecycle — folds the old
// Status + Collection columns into ONE value per env. Colours (BE-defined):
// Connected=green, Collection Pending=yellow, Collection stopped=orange,
// Deleted=grey. StatusDot COLOR_MAP: ok=green, inactive=yellow, warn=orange,
// idle=grey — so this maps 1:1.
const LIFECYCLE_VARIANT: Record<string, StatusDotVariant> = {
  active: "ok",
  collection_pending: "inactive",
  collection_stopped: "warn",
  // IR-1487 (Rotem 62703): Archived groups exist on production since IR-1615
  // but the tab never listed the state, so they were filtered out of the list
  // entirely — see DEFAULT_LIFECYCLE_FILTER below.
  archived: "archived",
  deleted: "idle",
  // IR-1616 stage 2/3 (Rotem/Ziv 63062, Yaniv 2026-09-30): the COLLECTION
  // column's archived-group values (group_lifecycle.collection_state_payload)
  // — a disjoint value set from the Lifecycle-axis ones above, rendered
  // through the same LifecycleBadge/lifecycleVariant. Without these three the
  // dot silently fell back to idle (grey) for every archived group's
  // Collection cell, which is exactly the "not verified with a screenshot"
  // gap the stage-2 report flagged.
  stop_pending: "warn",
  stopped: "warn",
  stop_failed: "down",
};
const LIFECYCLE_FALLBACK_LABEL: Record<string, string> = {
  active: "Connected",
  collection_pending: "Collection Pending",
  collection_stopped: "Collection stopped",
  archived: "Archived",
  deleted: "Deleted",
  stop_pending: "Stop Pending",
  stopped: "Stopped",
  stop_failed: "Stop Failed",
};
export function lifecycleVariant(lc: string | null | undefined): StatusDotVariant {
  return lc ? (LIFECYCLE_VARIANT[lc] ?? "idle") : "idle";
}

/**
 * The ingestion ring's denominator.
 *
 * IR-1228 r5 (Rotem, 2026-09-09): "no- same nuber- the inner rings should
 * reflect the number actually landed in the system out of 492! if the group is
 * not connected - it can never send data". The backend counts ingestion over
 * the CONNECTED groups only, so the slices sum to `ingestion_total`.
 *
 * A named function rather than an inline `??` chain because the fallback is the
 * interesting part and it is otherwise untestable: `total` is a proportion
 * input to the donut, never drawn as text, so a wrong denominator is invisible
 * in the DOM and shows up only as arcs that do not fill the ring.
 *
 * The fallback matters because iris-ops reaches production ahead of
 * iris-backend: against an older backend this returns `lcp_total`, which is
 * r3's behaviour — wrong in the way she reported, but coherent — rather than 0,
 * which would divide by zero and draw nothing.
 */
export function ingestionRingTotal(
  breakdown: { ingestion_total?: number; lcp_total?: number } | undefined,
): number {
  return breakdown?.ingestion_total ?? breakdown?.lcp_total ?? 0;
}

// IR-1431 (Rotem 08-25): the tab opens with everything EXCEPT Deleted selected.
//
// IR-1425 r10: the Lifecycle filter now filters the Lifecycle COLUMN — the
// matrix's 10 values, sent as `lcp=`. The default is every value but Historic
// (`DEFAULT_LCP_FILTER`): an active `lcp=` lifts the backend's Historic hide,
// Historic is not in the set so it stays hidden, and a deleted group has
// `lcp === null` so it never matches — the IR-1431 default is preserved
// without a "Deleted" option (deleted is not a lifecycle value). Re-exported
// under the old name so the tests that pin the default keep their import.
// There is no URL state for these filters, so nothing to migrate.
export const DEFAULT_LIFECYCLE_FILTER: string[] = DEFAULT_LCP_FILTER;

// IR-1431 (Rotem 08-25): "the added groups number isn't accurate — the analysts
// do add groups". The number was always arithmetically right; the LABEL implied
// something it never measured. "New" reads as "newly added by us"; the counter
// is stamped when the group row is first created in IRIS, which happens when an
// avatar reports the group — before any data arrives. So the tile is renamed to
// "Added" and carries an explanation, and each window now shows how many of
// those groups actually produced messages (prod 2026-08-25: 78 added over 30
// days, 63 collecting).
export const ADDED_TILE_TOOLTIP =
  "Counted when the group first appears in IRIS — the moment an avatar reports it, " +
  "not when it first sends data. 'collecting' is how many of those groups have " +
  "produced messages since. Follows the filters below; refreshes every 2 minutes.";

export type AddedTile = {
  key: string;
  label: string;
  value: number;
  /** null when the backend predates IR-1431 and omits the split. */
  collecting: number | null;
  color: string;
};

/** Compose the three Added tiles. Exported for test — the caption is the part
 *  that must degrade cleanly when an older backend omits `collecting_*`. */
export function buildAddedTiles(stats: {
  new_24h: number;
  new_7d: number;
  new_30d: number;
  collecting_24h?: number;
  collecting_7d?: number;
  collecting_30d?: number;
}): AddedTile[] {
  return [
    {
      key: "24h",
      label: "Added · 24h",
      value: stats.new_24h,
      collecting: stats.collecting_24h ?? null,
      color: "var(--iris-neon-cyan)",
    },
    {
      key: "7d",
      label: "Added · 7d",
      value: stats.new_7d,
      collecting: stats.collecting_7d ?? null,
      color: "var(--iris-neon-purple)",
    },
    {
      key: "30d",
      label: "Added · 30d",
      value: stats.new_30d,
      collecting: stats.collecting_30d ?? null,
      color: "var(--iris-neon-magenta)",
    },
  ];
}

/** Caption under an Added tile: "63 collecting", or null when unavailable. */
export function addedTileCaption(tile: AddedTile): string | null {
  if (tile.collecting === null) return null;
  return `${tile.collecting.toLocaleString()} collecting`;
}

// One derived-lifecycle badge (dot + coloured label). `label`/`color` come from
// the BE (prod_/dev_lifecycle_label/_color) with a local fallback so the cell
// still renders if an older BE omits them.
export function LifecycleBadge({
  value,
  label,
}: {
  value: string | null;
  label: string | null;
}) {
  if (!value) return <span style={{ color: "var(--iris-fg-5)" }}>{"—"}</span>;
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
      {/* IR-1228 r5 (Rotem, 2026-09-08): "collection - remove the circle
          animation". StatusDot pulses by DEFAULT (`pulse = true`), so this was
          never a deliberate choice here — the badge inherited it. On a 25-row
          page it is 25 things breathing at once, which reads as "something is
          happening" on a column that is merely stating a fact. */}
      <StatusDot variant={lifecycleVariant(value)} size={7} pulse={false} />
      {/* IR-1227 (Rotem 53972): colour the DOT only, not the label text —
          the neutral label reads cleaner; the state colour lives in the dot. */}
      <span style={{ color: "var(--iris-fg-2)" }}>
        {label ?? LIFECYCLE_FALLBACK_LABEL[value] ?? value}
      </span>
    </span>
  );
}

// IR-1465: the "Collection" column is PRODUCTION-ONLY.
//
// IR-1227 55* made it a cross-env merge — "the collection is one status" — to
// collapse a boundary straddle: the recency basis then was the per-env
// ingestion clock, so the same scrape could age out of the window in one env
// before the other. IR-1400 r7 moved that basis onto the message's own
// timestamp, which is identical in both envs, and the merge stopped collapsing
// an artefact and started masking real coverage gaps — an OR can only ever make
// a group look MORE alive than production is. On prod 2026-09-08 it painted
// "Active" on 5 rows the Lifecycle filter correctly excluded.
//
// So the badge now reads the production lifecycle, exactly like the Lifecycle
// filter, the "Active groups" tile and the matching count. The dev-side signal
// is not lost — it is stated as its own marker (below) rather than folded into
// a badge that would be claiming production is collecting.

// A secondary annotation inside the Collection cell: something true about this
// row that the badge itself cannot say. Shared by both markers so they cannot
// drift apart visually — two differently-weighted labels in one cell read as a
// rendering bug. `title` carries supporting detail only; the label stands alone
// (a compact label must not depend on hover to be understood), and it never
// wraps to a second line.
function CellMarker({ text, title }: { text: string; title: string }) {
  return (
    <span title={title} style={{ color: "var(--iris-fg-3)", fontSize: 10, whiteSpace: "nowrap" }}>
      {text}
    </span>
  );
}

// IR-1425 r12b (Rotem 2026-10-12, comment 64191): a collector-reported
// instant, shown in the VIEWER's local time with the zone printed beside it —
// "store and transfer UTC with a zone marker, display in the user's local time
// with the zone visible". One component for both date cells and the detail
// panel so the stamp, the zone and the optional kind line cannot drift apart.
//
//   - null / undefined / unparseable → a single em dash and NO kind line (the
//     designed degradation on a backend that predates r12b).
//   - the zone label is CellMarker-sized (10px), the same muted colour, printed
//     once per cell, `nowrap` so it is never clipped or wrapped away from the
//     stamp it qualifies.
//   - `kindLabel` (Last Collection only) is a second muted line in the same
//     size as the Collection cell's own sub-status line
//     (`collection-axis-second-line` in lifecycle-matrix.tsx: 10.5) — one
//     visual language for secondary cell text.
const COLLECTOR_STAMP_SUBLINE_SIZE = 10.5;
function CollectorStamp({
  iso,
  title,
  kindLabel,
}: {
  iso: string | null | undefined;
  title: string;
  kindLabel?: string | undefined;
}) {
  const stamp = fmtLocalDateTime(iso);
  if (stamp === "\u2014") {
    return (
      <span style={{ color: "var(--iris-fg-3)" }} title={title}>
        {"\u2014"}
      </span>
    );
  }
  return (
    <span
      title={title}
      style={{ display: "inline-flex", flexDirection: "column", gap: 1, color: "var(--iris-fg-3)" }}
    >
      <span style={{ whiteSpace: "nowrap" }}>
        {stamp} <span style={{ fontSize: 10, whiteSpace: "nowrap" }}>{viewerZoneLabel(iso)}</span>
      </span>
      {kindLabel ? (
        <span style={{ fontSize: COLLECTOR_STAMP_SUBLINE_SIZE, lineHeight: 1.3 }}>{kindLabel}</span>
      ) : null}
    </span>
  );
}

// IR-1465: with a production-only badge, a PROD delete is no longer hidden —
// the badge says "Deleted" itself, so the old "Deleted (prod)" marker would
// just repeat it. A DEV-side delete is still something the badge cannot show,
// so that is the one case this marker keeps covering (and only when prod exists
// and is alive; both-deleted or no-mate → null, the badge already reads right).
export function perEnvDeletedMarker(
  prodLifecycle: string | null | undefined,
  devLifecycle: string | null | undefined,
): string | null {
  if (devLifecycle !== "deleted") return null;
  if (prodLifecycle == null || prodLifecycle === "deleted") return null;
  return "Deleted (dev)";
}

// The "Collection" cell: the matrix Collection axis (IR-1425 r10 — one of 8
// states with what was observed / next sample / stop reason under it) when
// the backend serves `collection_axis`; today's merged-lifecycle badge when it
// does not (pre-port backend, or `null` for a deleted group). The two markers
// for what production alone cannot tell you render in both paths.
export function CollectionCell({ group }: { group: UnifiedGroupRecord }) {
  const deletedMarker = perEnvDeletedMarker(group.prod_lifecycle, group.dev_lifecycle);
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
      {typeof group.collection_axis === "string" ? (
        <CollectionAxisChip fields={group} secondLine />
      ) : (
        <LifecycleBadge
          value={group.collection_lifecycle}
          label={group.collection_lifecycle_label}
        />
      )}
      {group.dev_only_delivering ? (
        <CellMarker
          text="Dev only"
          title="Collecting into the development environment only — production has received nothing from this group inside the activity window. Typical of groups covered by a V2 avatar, which uploads to the dev location."
        />
      ) : null}
      {deletedMarker ? (
        <CellMarker
          text={deletedMarker}
          title="Deleted in the development environment only — the Collection status shown is production's"
        />
      ) : null}
    </span>
  );
}

// IR-1228 r5 (Rotem, 2026-09-08): "do the same circle and color coding in
// Coverage & INgestion collumn". Both rendered as bare grey words while
// Collection beside them carried a coloured dot, so three columns describing
// three layers of the same pipeline looked like one live column and two inert
// ones.
//
// The dot is ADDITIVE — the word stays. Colour alone must never be the carrier
// of a state (it fails for colour-blind readers and disappears in a screenshot
// pasted into Jira), and these two columns are read side by side precisely to
// compare their words.
const COVERAGE_VARIANT: Record<string, StatusDotVariant> = {
  covered: "ok",
  coverage_stopped: "warn",
  never_covered: "idle",
};

// IR-1627 (Rotem, IR-1602 c62300): "so it actually divides messages to
// Messages- Complete, Messages Partial".
//
// 'unknown' is deliberately ABSENT from this map rather than mapped to a muted
// dot. PipelineStatusCell draws no dot for a value it cannot find, and an
// unknown must not look like a measured state — it is the same evidence rule
// Coverage follows. Never let it read as Complete.
const MESSAGES_QUALITY_VARIANT: Record<string, StatusDotVariant> = {
  complete: "ok",
  // The attention state: the walk stopped early. What it collected was kept,
  // so this is "incomplete", not "failed" — warn, not err.
  partial: "warn",
  // IR-1627 (Rotem c63064): N.A after a confirmed stop is a settled, correct
  // state — not a problem and not an absence — so it draws the neutral
  // "idle" dot, same family as Coverage's `never_covered`. Explicit here
  // rather than left to PipelineStatusCell's `?? "idle"` fallback: the
  // fallback exists for genuinely unmapped values, and N.A must never
  // silently become "whatever idle means today" by omission.
  not_applicable: "idle",
};

/**
 * IR-1425 r12c (Rotem 2026-10-12): why Messages Read is Not Applicable, from
 * the row's own state — the brief's four causes, first match wins. The
 * tooltip must say WHY, never the fixed "confirmed stopped" for all four.
 */
function notApplicableReason({
  collectionAxis,
  lcp,
  observation,
}: {
  collectionAxis: string | null | undefined;
  lcp: string | null | undefined;
  observation: string | null | undefined;
}): string {
  if (collectionAxis === "stopped") return "collection is confirmed stopped";
  if (lcp === "historic" || collectionAxis === "historic_import")
    return "historic import, not live collection";
  if (lcp === "waiting_approval" || collectionAxis === "collection_pending")
    return "waiting for approval, nothing collected yet";
  if (observation === "not_checked" || collectionAxis === "not_checked")
    return "the latest result is Not Checked";
  return "no current reading applies";
}

/**
 * IR-1627 — the Messages cell: how complete the last message collection was.
 *
 * IR-1425 r12c (Rotem 2026-10-12) — "labelled history": a Not Applicable
 * keeps the previous settled answer visible under it ("was Complete · <local
 * time + zone>"), and the tooltip says WHY it is N/A (confirmed stop /
 * historic / waiting approval / latest result Not Checked). A
 * `could_not_check` round reads Unknown with the observation named in the
 * tooltip instead of "does not report".
 *
 * 🔑 "In Process" is NOT a fourth quality value (Yaniv, 2026-09-16): it is a
 * transient indicator, and *"while In Process is shown, the previous settled
 * answer remains visible until the new result is written"*. So it renders as a
 * marker BESIDE the word, never in place of it. A group being collected right
 * now still shows the answer it had — which matters because a group spends
 * about a quarter of its time mid-collection (measured on prod: collected every
 * ~4h, answer written ~40 min in).
 *
 * 🪤 Deliberately NOT a live region. The guidance is one atomic contextual
 * status, and never competing live regions — this table renders ~25-50 rows and
 * polls, so marking every cell live would make a screen reader unusable. The
 * full state is instead in one accessible title on the cell.
 */
export function MessagesQualityCell({
  quality,
  label,
  stopReason,
  inProcess,
  at,
  previous,
  previousLabel,
  previousAt,
  observation,
  observationLabel,
  collectionAxis,
  lcp,
  avatars,
}: {
  quality: string | null;
  label: string | null;
  stopReason: string | null;
  inProcess: boolean;
  at: string | null;
  /** IR-1425 r12c: the previous settled answer, served only beside not_applicable. */
  previous?: string | null | undefined;
  previousLabel?: string | null | undefined;
  previousAt?: string | null | undefined;
  /** IR-1425 r12c: the row's observation / axis / lifecycle, for the tooltip's why. */
  observation?: string | null | undefined;
  observationLabel?: string | null | undefined;
  collectionAxis?: string | null | undefined;
  lcp?: string | null | undefined;
  /**
   * IR-1425 r15 (Rotem 64808): every avatar's OWN result for the group's latest
   * round. Listed under the group value only when TWO or more avatars read the
   * group — with 0 or 1 the cell is exactly what it was.
   */
  avatars?: MessagesQualityAvatar[] | null | undefined;
}) {
  const avatarLines = avatars && avatars.length >= 2 ? avatars : null;
  // One sentence carrying the whole state, for the tooltip and the accessible
  // name — rather than a bare word the reader has to interpret.
  const parts: string[] = [];
  const previousText = previous ? (previousLabel ?? previous) : null;
  const showPrevious = quality === "not_applicable" || quality === "unknown";
  const previousPhrase = previousAt
    ? `was ${previousText} as of ${fmtLocalDateTimeWithZone(previousAt)}`
    : `was ${previousText}`;
  if (quality === "complete") parts.push("Everything new was read");
  else if (quality === "partial") parts.push("Stopped early; what it collected was kept");
  // IR-1627 (Rotem c63064): N.A after a confirmed stop — distinct from
  // Unknown (a collector that does not report). Checked before the
  // Unknown fallback so the two can never share a sentence. IR-1425 r12c:
  // the sentence says WHY, then names the previous answer and its time.
  else if (quality === "not_applicable") {
    parts.push(`Not applicable: ${notApplicableReason({ collectionAxis, lcp, observation })}`);
    if (previousText) parts.push(previousPhrase);
  }
  // IR-1425 r12c: a could_not_check round is Unknown WITH its reason.
  else if (observation === "could_not_check")
    parts.push(
      `Unknown: ${observationLabel ?? "Could Not Check"} — the collector could not read this group on its latest attempt`,
    );
  else parts.push("This collector does not report yet");
  // IR-1753 r12 (Rotem 66342): Unknown is not a current reading, so it also
  // names the last reading and its original date, exactly as Not Applicable.
  if (showPrevious && quality !== "not_applicable" && previousText) parts.push(previousPhrase);
  if (stopReason) parts.push(`reason: ${stopReason}`);
  if (at) parts.push(`as of ${new Date(at).toLocaleString()}`);
  if (inProcess) parts.push("a new collection is running now");
  const title = parts.join(" \u00b7 ");

  // The OUTER span carries title + aria-label so one element holds the whole
  // state (the history line under it is part of that state).
  return (
    <span
      style={{ display: "inline-flex", flexDirection: "column", gap: 1, minWidth: 0 }}
      title={title}
      aria-label={`Messages: ${label ?? "Unknown"}${inProcess ? ", collecting now" : ""}`}
    >
      <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
        {quality && quality !== "unknown" ? (
          <PipelineStatusCell
            value={quality}
            label={label ?? quality}
            variants={MESSAGES_QUALITY_VARIANT}
          />
        ) : (
          // IR-1425 (Harel 64191 / Matrix law 11): Unknown is a named state and
          // is never a dash. The WORD, in the muted colour the dash used, with
          // no dot: it must never look like Complete or Partial. Kept here and
          // not in the shared PipelineStatusCell, whose null -> dash is the
          // Coverage and Ingestion contract.
          <span style={{ color: "var(--iris-fg-5)" }}>{label ?? "Unknown"}</span>
        )}
        {inProcess ? (
          <CellMarker
            text="collecting"
            title="A collection is running for this group right now. The value shown is the previous result; a new one is on its way."
          />
        ) : null}
      </span>
      {showPrevious && previousText ? (
        <span
          data-testid="messages-quality-previous"
          title={previousAt ?? undefined}
          style={{
            fontSize: 10.5,
            color: "var(--iris-fg-4)",
            lineHeight: 1.3,
            whiteSpace: "normal",
          }}
        >
          {`was ${previousText}${previousAt ? ` · ${fmtLocalDateTimeWithZone(previousAt)}` : ""}`}
        </span>
      ) : null}
      {avatarLines
        ? avatarLines.map((a, i) => (
            <MessagesQualityAvatarLine
              // The backend sorts by avatar name and one avatar reports once per
              // round; the index guards the nameless entry.
              key={`${a.avatar_name ?? "unnamed"}-${i}`}
              entry={a}
            />
          ))
        : null}
    </span>
  );
}

/**
 * IR-1425 r15: one avatar's own result, same small sub-line style as the
 * "was Complete" history line. The label is the AVATAR's, never the group's;
 * an unknown is the word Unknown (never blank, never Complete — Iron Law 11),
 * a partial carries its stop reason, a failed read carries the backend's
 * could-not-check note.
 */
function MessagesQualityAvatarLine({ entry }: { entry: MessagesQualityAvatar }) {
  // A failed read is Unknown whatever its raw `messages_quality` says (dev has
  // Telegram rows with error_code set AND messages_complete = true): show the
  // backend's `counted_quality` (Unknown for a failed report), never the raw
  // value, and never the raw value's label.
  const own = entry.failed
    ? (entry.counted_quality ?? "unknown")
    : (entry.messages_quality ?? "unknown");
  const word =
    (entry.failed ? null : entry.messages_quality_label) ??
    (own === "complete" ? "Complete" : own === "partial" ? "Partial" : "Unknown");
  const detail: string[] = [];
  if (own === "partial" && entry.messages_stop_reason) detail.push(entry.messages_stop_reason);
  if (entry.failed && entry.messages_quality_note) detail.push(entry.messages_quality_note);
  const name = entry.avatar_name ?? "unnamed avatar";
  const text = `${name}: ${word}${detail.length ? ` · ${detail.join(" · ")}` : ""}`;
  return (
    <span
      data-testid="messages-quality-avatar-line"
      data-avatar={entry.avatar_name ?? ""}
      data-quality={own}
      title={
        entry.messages_quality_at
          ? `${text} · as of ${new Date(entry.messages_quality_at).toLocaleString()}`
          : text
      }
      style={{
        fontSize: 10.5,
        color: "var(--iris-fg-4)",
        lineHeight: 1.3,
        whiteSpace: "normal",
      }}
    >
      {text}
    </span>
  );
}

function riskColor(score: number | null): string {
  if (score == null) return "var(--iris-fg-5)";
  if (score >= 70) return "var(--iris-neon-pink)";
  if (score >= 40) return "var(--iris-neon-orange)";
  return "var(--iris-neon-cyan)";
}

function fmtDate(iso: string | null): string {
  if (!iso) return "\u2014";
  return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

// IR-661 Task 11.2: per-env count formatting for the Members + Messages
// columns. Compact notation (e.g. 1.2K, 3.4M) keeps the columns tight.
const COMPACT_FMT = new Intl.NumberFormat(undefined, {
  notation: "compact",
  maximumFractionDigits: 1,
});
function fmtCompact(n: number | null | undefined): string {
  if (n == null) return "\u2014";
  return COMPACT_FMT.format(n);
}
function fmtPair(prod: number | null | undefined, dev: number | null | undefined): string {
  return `${fmtCompact(prod)} / ${fmtCompact(dev)}`;
}

// IR-1147 reopen: the inline join-link editor was removed with the Join Link
// column (Rotem: "we can remove the join link from the table"). Join links are
// still editable from the group edit dialog.

// IR-1147 reopen: map the entity app_type (e.g. "telegram") to the Join Links
// platform code the PlatformIcon brand glyphs use ("tg"/"wa"/"signal").
// IR-1479 r3: the helper moved to PlatformIcon.tsx so the Avatars table can
// share it without a circular import; re-exported here for existing callers.
export { platformToIconCode };

// ─── Detail Panel ───

// Exported for direct rendering in tests (IR-1425 r12b): on origin/main no UI
// path ever sets `selected` to a row, so the panel is otherwise unreachable.
export function GroupDetailPanel({
  group,
  onDeleteRequested,
  onEditRequested,
  onRemoveRequested,
  removePending,
}: {
  group: UnifiedGroupRecord;
  onDeleteRequested: () => void;
  onEditRequested: () => void;
  onRemoveRequested: () => void;
  removePending: boolean;
}) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16, padding: 16, fontSize: 12 }}>
      <div>
        <Kicker variant="muted" style={{ marginBottom: 4 }}>
          Display Name
        </Kicker>
        <div style={{ color: "var(--iris-fg-1)" }}>{group.display_name}</div>
      </div>

      {group.scrapping_id && (
        <div>
          <Kicker variant="muted" style={{ marginBottom: 4 }}>
            Scrapping ID
          </Kicker>
          <div
            style={{ fontFamily: "var(--iris-font-mono)", fontSize: 11, color: "var(--iris-fg-3)" }}
          >
            {group.scrapping_id}
          </div>
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <div>
          <Kicker variant="muted" style={{ marginBottom: 4 }}>
            Prod Status
          </Kicker>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <StatusDot variant={statusVariant(group.prod_status)} size={7} />
            <span>{group.prod_status ?? "\u2014"}</span>
          </div>
        </div>
        <div>
          <Kicker variant="muted" style={{ marginBottom: 4 }}>
            Dev Status
          </Kicker>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <StatusDot variant={statusVariant(group.dev_status)} size={7} />
            <span>{group.dev_status ?? "\u2014"}</span>
          </div>
        </div>
        <div>
          <Kicker variant="muted" style={{ marginBottom: 4 }}>
            Dev Members
          </Kicker>
          <div>{group.dev_members?.toLocaleString() ?? "\u2014"}</div>
        </div>
        <div>
          <Kicker variant="muted" style={{ marginBottom: 4 }}>
            Risk Score
          </Kicker>
          <div>{group.risk_score ?? "\u2014"}</div>
        </div>
      </div>

      {group.classification && (
        <div>
          <Kicker variant="muted" style={{ marginBottom: 4 }}>
            Classification
          </Kicker>
          <div style={{ color: "var(--iris-fg-3)" }}>{group.classification}</div>
        </div>
      )}

      {group.avatars.length > 0 && (
        <div>
          <Kicker variant="muted" style={{ marginBottom: 8 }}>
            Avatars ({group.avatars.length})
          </Kicker>
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            {group.avatars.map((av) => (
              <div
                key={av.avatar_id}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  borderRadius: "var(--iris-r-md)",
                  border: "1px solid var(--iris-line-1)",
                  background: "var(--iris-bg-3)",
                  padding: "8px 12px",
                }}
              >
                <div style={{ display: "flex", flexDirection: "column" }}>
                  <span>{av.avatar_name}</span>
                  {/* IR-1147 reopen: mobile number under the device name (Tomer). */}
                  <span style={{ fontSize: 11, color: "var(--iris-fg-3)" }}>
                    {fmtAvatarPhone(av.phone_number)}
                  </span>
                </div>
                <Chip style={{ fontSize: 10, padding: "2px 8px" }}>{av.platform}</Chip>
              </div>
            ))}
          </div>
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <div>
          <Kicker variant="muted" style={{ marginBottom: 4 }}>
            Last Active
          </Kicker>
          <div style={{ color: "var(--iris-fg-3)" }}>{fmtDate(group.last_active_at)}</div>
        </div>
        <div>
          {/* IR-1425 r12b: the collector's latest attempt (`last_monitoring_at`), local
              time + zone — never the ingestion heartbeat `last_monitoring_time`. */}
          <Kicker variant="muted" style={{ marginBottom: 4 }}>
            Last Monitoring
          </Kicker>
          <div style={{ color: "var(--iris-fg-3)" }}>
            <CollectorStamp
              iso={group.last_monitoring_at}
              title="The collector's latest attempt to reach this group, failed attempts included — not an ingestion stamp. Shown in your local time; the zone is printed beside it."
            />
          </div>
        </div>
      </div>

      {/* IR-1045 P4b: coverage status + Remove Group (terminate, status-only). */}
      <div>
        <Kicker variant="muted" style={{ marginBottom: 4 }}>
          Coverage
        </Kicker>
        <div
          style={{
            color: isCoverageTerminated(group) ? "var(--iris-neon-pink)" : "var(--iris-fg-3)",
          }}
        >
          {group.coverage_status ?? "—"}
        </div>
      </div>

      <div
        style={{
          borderTop: "1px solid var(--iris-line-1)",
          paddingTop: 12,
          display: "flex",
          gap: 8,
          justifyContent: "flex-end",
          flexWrap: "wrap",
        }}
      >
        <button type="button" className="iris-btn" onClick={onEditRequested}>
          Edit group
        </button>
        <button
          type="button"
          className="iris-btn"
          disabled={removePending || isCoverageTerminated(group)}
          title="Terminate coverage: marks the group 'Coverage Terminated' (status-only; does not unassign the avatar)"
          onClick={onRemoveRequested}
        >
          {isCoverageTerminated(group) ? "Coverage terminated" : "Remove group"}
        </button>
        <button type="button" className="iris-btn iris-btn--primary" onClick={onDeleteRequested}>
          Delete group
        </button>
      </div>
    </div>
  );
}

// IR-1555: the hover text on both Active tiles once the matrix figure is served.
// It names both lines of the split, so "Roster only" is never read as a share of
// the headline.
const ACTIVE_SPLIT_TITLE =
  "Active (the big number) and Messages: groups whose Lifecycle is Active AND whose last message is inside this window — a roster alone is not Active (matrix). Roster only: groups that sent a member list in this window but no message; shown beside Active, not counted in it. Does not follow table filters.";

/**
 * IR-1555 (Rotem, 2026-10-25): "write it in the same tile Active — Messages —
 * roster only". Two labelled lines under the headline. A text label on every
 * line, digits tabular so the two counts align — no colour carries meaning.
 * `rosterOnly` null = the backend has no roster source, printed as "—" rather
 * than a 0 that would claim "no roster-only groups".
 */
function ActiveSplit({
  testId,
  messages,
  rosterOnly,
}: {
  testId: string;
  messages: number;
  rosterOnly: number | null;
}) {
  return (
    <dl data-testid={testId} style={{ margin: "3px 0 0", fontSize: 11, lineHeight: 1.35 }}>
      {(
        [
          ["Messages", messages, "var(--iris-fg-2)"],
          ["Roster only", rosterOnly, "var(--iris-fg-3)"],
        ] as const
      ).map(([label, count, color]) => (
        <div
          key={label}
          style={{
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "space-between",
            columnGap: 6,
            color,
          }}
        >
          <dt>{label}</dt>
          <dd style={{ margin: 0, fontVariantNumeric: "tabular-nums", overflowWrap: "anywhere" }}>
            {count == null ? "—" : count.toLocaleString()}
          </dd>
        </div>
      ))}
    </dl>
  );
}

// ─── Main ───

const PAGE_SIZE = 25;

/**
 * IR-1228 (Rotem c61856): "platform - same width as status column". Platform is
 * a single 16px glyph and Lifecycle is a short label, so both sit comfortably
 * here; one constant means a later change to either cannot desynchronise them.
 */
export const STATUS_COL_WIDTH = 140;

/**
 * IR-1228 r5 (Rotem c61961): "group ID - limiy size to '1951027298'" — her
 * example is a 10-digit Telegram id. Signal and WhatsApp ids are 44-character
 * base64, and one of those in the page was widening the column for every row.
 */
export const GROUP_ID_COL_WIDTH = 110;

/**
 * IR-1228 (Rotem c61856): "name - show translation in tooltip".
 *
 * Returns the translation only when there IS one. A `group_name_en` equal to
 * the raw name is the IR-1142 signature of an English name round-tripped
 * through Google Translate, and echoing it back in a tooltip would present that
 * bug as a feature — so it is treated as no translation at all.
 */
export function groupNameTranslation(group: {
  display_name: string;
  name_en?: string | null;
}): string | null {
  const en = group.name_en?.trim();
  if (!en) return null;
  return en.toLowerCase() === group.display_name.trim().toLowerCase() ? null : en;
}

/**
 * The group name cell. The name is a button (it opens the group card), which is
 * also what makes the translation reachable by keyboard — a `title`-only
 * disclosure would leave it to hover alone.
 */
function GroupNameButton({
  group,
  onOpen,
}: {
  group: UnifiedGroupRecord;
  onOpen: () => void;
}) {
  const translation = groupNameTranslation(group);
  const button = (
    <button
      type="button"
      onClick={onOpen}
      title={translation ? undefined : "Open the group card"}
      style={{
        background: "none",
        border: "none",
        padding: 0,
        cursor: "pointer",
        textAlign: "left",
        font: "inherit",
        color: "var(--iris-fg-1)",
        fontWeight: 500,
      }}
    >
      {group.display_name}
    </button>
  );
  if (!translation) return button;
  return (
    <Tooltip>
      <TooltipTrigger asChild>{button}</TooltipTrigger>
      <TooltipContent side="bottom" style={{ maxWidth: 320 }} data-testid="group-name-translation">
        {translation}
      </TooltipContent>
    </Tooltip>
  );
}

const PLATFORM_OPTIONS = ["telegram", "whatsapp", "signal", "other"];
// Column key → groups_unified sort key (SORT_FIELDS_UNIFIED). The prod_status
// column maps to the entity status; members/messages to the GAD counts.
const SORT_KEY_MAP: Record<string, string> = {
  display_name: "name",
  // IR-1147 reopen: Platform column sortable (Rotem round-2).
  group_application_type: "platform",
  created_at: "created_at",
  // IR-1425 r12b: the Last Monitoring column sorts on the collector's latest
  // attempt, not the ingestion heartbeat (`last_monitoring_time`).
  last_monitoring_at: "last_monitoring_at",
  // IR-1765 r2: Last Active sorts server-side with Unknown last in both directions (IR-1228 NULLS LAST).
  last_active_at: "last_active_at",
  risk_score: "risk_score",
  prod_status: "status",
  __members: "member_count",
  __messages: "message_count",
};

function NotesCell({ group }: { group: UnifiedGroupRecord }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(group.notes ?? "");
  const setNotes = useSetGroupNotes();
  const save = () =>
    setNotes.mutate({ id: group.id, notes: draft }, { onSuccess: () => setEditing(false) });

  if (!editing) {
    return (
      <div
        style={{ display: "flex", alignItems: "center", gap: 6, maxWidth: 220 }}
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
      >
        <span
          title={group.notes ?? ""}
          style={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            color: group.notes ? "var(--iris-fg-2)" : "var(--iris-fg-5)",
            fontStyle: group.notes ? "normal" : "italic",
          }}
        >
          {group.notes || "Add note…"}
        </span>
        <button
          type="button"
          aria-label="Edit note"
          className="iris-btn"
          style={{ padding: "2px 6px", fontSize: 10, flexShrink: 0 }}
          onClick={(e) => {
            e.stopPropagation();
            setDraft(group.notes ?? "");
            setEditing(true);
          }}
        >
          Edit
        </button>
      </div>
    );
  }

  return (
    <div
      style={{ display: "flex", alignItems: "center", gap: 4 }}
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.stopPropagation()}
    >
      <input
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        placeholder="Note…"
        className="iris-input"
        style={{ height: 24, fontSize: 11, padding: "0 8px", minWidth: 140 }}
        // biome-ignore lint/a11y/noAutofocus: inline editor focus on open
        autoFocus
        onKeyDown={(e) => {
          if (e.key === "Enter") save();
          if (e.key === "Escape") {
            setDraft(group.notes ?? "");
            setEditing(false);
          }
        }}
      />
      <button
        type="button"
        className="iris-btn"
        style={{ height: 24, padding: "0 8px", fontSize: 10 }}
        disabled={setNotes.isPending}
        onClick={save}
      >
        Save
      </button>
      <button
        type="button"
        className="iris-btn"
        style={{ height: 24, padding: "0 8px", fontSize: 10 }}
        onClick={() => {
          setDraft(group.notes ?? "");
          setEditing(false);
        }}
      >
        {"✕"}
      </button>
    </div>
  );
}

// IR-1045 P4b: the manual "Remove Group" coverage override. Exported for unit
// testing the chip / disabled-button gating.
export function isCoverageTerminated(g: Pick<UnifiedGroupRecord, "coverage_status">): boolean {
  return g.coverage_status === "coverage_terminated";
}

export function GroupsUnifiedTab({ initialSearch = "" }: { initialSearch?: string } = {}) {
  const [page, setPage] = useState(1);
  // IR-1697: opened from an Avatars failed-groups drill-in, the search starts on
  // that group's scrapping_id.
  const [search, setSearch] = useState(initialSearch);
  // IR-1045 Phase 1: platform/status multi-select + server-side sort. The
  // groups_unified endpoint comma-splits platform/status and accepts ?sort=.
  const [platformFilter, setPlatformFilter] = useState<string[]>([]);
  // IR-1227 (Rotem 53901): single Lifecycle filter replaces Status + Collection.
  // IR-1431 (Rotem 08-25): "can we make the table hide Deleted — select all
  // other statuses by default?" — so the default is every state EXCEPT deleted
  // rather than the empty "no filter" set. 334 of 4,373 prod groups are
  // deleted. Clearing the filter by hand still shows them.
  // IR-1425 r10: `lcpFilter` → `lcp=` (Lifecycle column), `collectionAxisFilter`
  // → `collection_axis=` (Collection column). The old `lifecycle=` /
  // `group_status=` params are no longer sent by this tab.
  const [lcpFilter, setLcpFilter] = useState<string[]>(DEFAULT_LIFECYCLE_FILTER);
  const [collectionAxisFilter, setCollectionAxisFilter] = useState<string[]>([]);
  // IR-1228 P5 (Rotem c61508: "Historic hidden by default"). Measured on prod
  // 2026-09-04: 2,176 of 4,051 non-deleted groups are Historic — 53.7% of the
  // screen was the legacy backlog P0 tagged. The BACKEND does the hiding; this
  // side simply does not ask for them unless the toggle is on.
  const [showHistoric, setShowHistoric] = useState(false);
  // IR-1228 P5: the group card (Rotem c61508, "Group card like the avatar
  // card"). Opened from the name, the same gesture as the Avatars drawer.
  const [cardGroup, setCardGroup] = useState<UnifiedGroupRecord | null>(null);
  // IR-1147 reopen: default sort = latest monitoring on top (Rotem round-3).
  // IR-1425 r12b: "latest monitoring" is now the collector's latest attempt
  // (`last_monitoring_at`, the BE's default sort too); initializing the state
  // mirrors it so the column shows the sort indicator from first paint.
  const [sortKey, setSortKey] = useState<string | null>("last_monitoring_at");
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");
  // IR-1384 (Sean): ONE "Added" filter holding both the quick windows and a
  // custom calendar range. They were two separate pills ("Added" + "Created")
  // over the same entity date, which read as "no custom range exists".
  const [added, setAdded] = useState<DateWindowValue>(EMPTY_DATE_WINDOW);
  const [avatarFilter, setAvatarFilter] = useState<string[]>([]);
  const [selected, setSelected] = useState<UnifiedGroupRecord | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<UnifiedGroupRecord | null>(null);
  // IR-661 Rotem round-2 (2026-05-20): wire the previously orphaned
  // GroupEditDialog into the detail panel.
  const [editTarget, setEditTarget] = useState<UnifiedGroupRecord | null>(null);
  const [activeJobs, setActiveJobs] = useState<Map<string, JobEntry>>(new Map());
  // IR-661 Task 12: bulk-delete selection state.
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  // IR-1426 r2 (Rotem 2026-08-24): the Reported filter. Replaces r1's
  // "Added groups" fold — that partitioned the current PAGE, so its count
  // described 50 rows while the tab claimed thousands. This one is a real
  // query param, so the rows, the count and `total` describe one population.
  const [onPhoneFilter, setOnPhoneFilter] = useState<string[]>([]);
  // IR-1627 (Rotem c63023): the "Messages Read" filter — same server-side,
  // capability-gated shape as onPhoneFilter above.
  const [messagesQualityFilter, setMessagesQualityFilter] = useState<string[]>([]);
  // IR-1697 r11: the Ingestion column filtered per environment, server-side as
  // `prod_ingestion=` / `dev_ingestion=`. Two states, never one merged list.
  const [prodIngestionFilter, setProdIngestionFilter] = useState<string[]>([]);
  const [devIngestionFilter, setDevIngestionFilter] = useState<string[]>([]);

  // Flat helper used by BOTH the single-delete Sheet AND the bulk
  // DeleteSelectedDialog. Registers up to two banner rows per delete
  // (PROD + DEV) when the dual-env response includes both job ids.
  const addJobEntries = (
    jobIds: { prod: string | null; dev: string | null },
    groupId: string,
    groupName: string,
  ) => {
    if (jobIds.prod) {
      setActiveJobs((prev) => {
        const next = new Map(prev);
        next.set(jobIds.prod as string, { groupId, groupName, env: "prod" });
        return next;
      });
    }
    if (jobIds.dev) {
      setActiveJobs((prev) => {
        const next = new Map(prev);
        next.set(jobIds.dev as string, { groupId, groupName, env: "dev" });
        return next;
      });
    }
  };

  // Single-delete Sheet keeps its existing UnifiedGroupRecord-based
  // signature; Bulk path calls addJobEntries directly with flat args.
  const onJobEnqueued = (
    jobIds: { prod: string | null; dev: string | null },
    group: UnifiedGroupRecord,
  ) => {
    addJobEntries(jobIds, group.id, group.display_name);
    setSelected((prev) => (prev?.id === group.id ? null : prev));
  };
  const removeJob = (jobId: string) => {
    setActiveJobs((prev) => {
      const next = new Map(prev);
      next.delete(jobId);
      return next;
    });
  };

  const toggleSelectId = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const allOnPageSelected = (rows: UnifiedGroupRecord[]) =>
    rows.length > 0 && rows.every((r) => selectedIds.has(r.id));
  const toggleSelectAllOnPage = (rows: UnifiedGroupRecord[]) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (rows.every((r) => next.has(r.id))) {
        for (const r of rows) next.delete(r.id);
      } else {
        for (const r of rows) next.add(r.id);
      }
      return next;
    });
  };

  const unifiedFilters: Parameters<typeof useUnifiedGroups>[0] = { page, page_size: PAGE_SIZE };
  if (search) unifiedFilters.search = search;
  if (platformFilter.length > 0) unifiedFilters.platform = platformFilter.join(",");
  // Unknown tokens are dropped client-side before sending (the backend drops
  // them too) so a stale selection cannot become a silent empty page.
  const lcpSelection = lcpFilter.filter((k) => (LCP_KEYS as readonly string[]).includes(k));
  const caxisSelection = collectionAxisFilter.filter((k) =>
    (CAXIS_KEYS as readonly string[]).includes(k),
  );
  if (lcpSelection.length > 0) unifiedFilters.lcp = lcpSelection.join(",");
  if (caxisSelection.length > 0) unifiedFilters.collection_axis = caxisSelection.join(",");
  if (showHistoric) unifiedFilters.include_historic = "1";
  Object.assign(unifiedFilters, addedFilterParams(added));
  if (avatarFilter.length > 0) unifiedFilters.avatar = avatarFilter.join(",");
  if (onPhoneFilter.length > 0) unifiedFilters.on_phone = onPhoneFilter.join(",");
  if (prodIngestionFilter.length > 0) unifiedFilters.prod_ingestion = prodIngestionFilter.join(",");
  if (devIngestionFilter.length > 0) unifiedFilters.dev_ingestion = devIngestionFilter.join(",");
  if (messagesQualityFilter.length > 0) {
    unifiedFilters.messages_quality = messagesQualityFilter.join(",");
  }
  if (sortKey && sortDirection) {
    unifiedFilters.sort = `${sortDirection === "desc" ? "-" : ""}${SORT_KEY_MAP[sortKey]}`;
  }
  const groupsQuery = useUnifiedGroups(unifiedFilters);
  const { isLoading, isError, refetch } = groupsQuery;
  // IR-1726 r3 (Rotem, 2026-10-14: "filtering on Lifecycle stucks everything —
  // it also affects the connected graphs that shouldn't be affected"). Her
  // Lifecycle = Archived request never reached the server (prod nginx: no
  // groups-unified line between 17:58:50Z and her retry at 18:03:42Z, which
  // returned the 147 rows). A failed query left `data` undefined, and every
  // block fell through to its "nothing here" text: tiles "—", "Collection axis
  // not served by this backend", "No groups to chart.", "No groups found" and
  // "0 groups". A load that failed rendered as an empty database.
  //
  // So the two kinds of number are split. `current` is the answer to THIS
  // filter set — rows, the filtered count — and is never borrowed from an older
  // request. `data` falls back to the last answer that did arrive, and is read
  // only by the blocks that ignore the filters by construction (the tiles under
  // IR-1400 r7, the IR-1482 rings, the filter options): their numbers are the
  // same whichever filter asked, so a failed or still-loading filter change no
  // longer blanks them. The banner above the table says what happened.
  const current = groupsQuery.data;
  const lastGoodGroups = useRef<typeof current>(undefined);
  useEffect(() => {
    if (current) lastGoodGroups.current = current;
  }, [current]);
  const data = current ?? lastGoodGroups.current;
  const loadFailed = isError && current === undefined;
  // IR-1147 reopen: avatar names for the per-column Avatar filter dropdown.
  const { data: avatarsList } = useFleetAvatars();
  const avatarOptions = (avatarsList ?? [])
    .map((a) => ({ value: a.name, label: a.name }))
    .sort((x, y) => x.label.localeCompare(y.label));
  // IR-1045 P4b: "Remove Group" → terminate coverage (status-only).
  const terminateCoverage = useTerminateCoverage();
  const lifecycleAction = useGroupLifecycleAction();
  const handleRemoveGroup = (group: UnifiedGroupRecord) => {
    if (
      !window.confirm(
        `Terminate coverage for "${group.display_name}"?\n\nThis marks the group "Coverage Terminated" (status-only — it does NOT unassign the avatar or delete any data).`,
      )
    ) {
      return;
    }
    terminateCoverage.mutate(group.id);
  };

  const results = current?.results ?? [];
  const total = current?.total ?? 0;
  const stats = data?.stats ?? { new_24h: 0, new_7d: 0, new_30d: 0 };
  // IR-1400 r7 (Rotem, 2026-08-24): "Filtering should not affect the total
  // numbers! add an indication for count". When the backend serves the
  // filter-free block, every tile reads it and the filtered figure becomes its
  // own labelled line instead of hiding inside a tile that looks absolute.
  // Absent (older prod backend) → the previous filtered behaviour, unchanged.
  const unfiltered = data?.summary?.unfiltered ?? null;
  const tilesAreFilterFree = unfiltered != null;
  // IR-1482 r2: the whole-population aggregate behind the lifecycle tiles and
  // the two rings. Filter-free by construction, like `unfiltered` above.
  const breakdown = data?.breakdown;
  // IR-1425 r10: Active = messages only when the backend serves it (see the
  // tiles). `null` from the backend means "not computed" and degrades too.
  const activeMessagesOnly = breakdown?.active_messages_only ?? null;
  // IR-1425 r10: Collection ring inputs, derived once from the served axis.
  const byAxis = breakdown?.by_collection_axis;
  const collectionAxisSlices = (byAxis ?? []).map((i) => ({ label: i.label, count: i.count }));
  const collectionAxisHex = new Map((byAxis ?? []).map((i) => [i.label, lcpSliceHexForItem(i)]));
  const collectionAxisColor = (label: string): string | undefined => collectionAxisHex.get(label);
  const collectionAxisCenter = byAxis?.find((i) => i.key === "collecting")?.count;
  // IR-1726 r3: "not served by this backend" only when an answer arrived
  // without the axis; no answer at all is a load state, not a backend verdict.
  const noAnswerYet = data === undefined ? (loadFailed ? "Couldn't load." : "Loading...") : null;
  const collectionAxisEmpty =
    noAnswerYet ??
    (byAxis === undefined
      ? "Collection axis not served by this backend"
      : "Nothing to chart for these groups.");
  const matching = current?.summary?.matching ?? total;
  // IR-1431: the three Added tiles + how many of each window actually collected.
  const addedTiles = buildAddedTiles(unfiltered ?? stats);
  const totalPages = Math.ceil(total / PAGE_SIZE);

  // IR-1426 r2: no client-side partition any more — the endpoint returns
  // exactly the rows the filter asked for, so what arrives is what is shown.
  const visibleRows = results;

  function resetPage() {
    setPage(1);
  }

  // Server-side sort: map the clicked column to a groups_unified sort key,
  // reset to page 1 so the user sees the new top of the ordering.
  function handleSort(columnKey: string, direction: SortDirection) {
    setSortKey(direction ? columnKey : null);
    setSortDirection(direction);
    resetPage();
  }

  // IR-1228 r5 (Rotem, 2026-09-08 "I dont see scroll", 2026-09-09 "The scroll is
  // weird and its not easy to use it - place it in the table header").
  //
  // The scrollbar existed; it was at the BOTTOM of a box taller than the screen,
  // so reaching it meant scrolling down past every row — and once she did, the
  // header was gone. A second scrollbar rides ABOVE the table, mirroring the
  // real one, so the control sits where the columns it moves are.
  const tableScrollRef = useRef<HTMLDivElement>(null);
  const topScrollRef = useRef<HTMLDivElement>(null);
  // Both widths, not just the content's: the bar is hidden when everything
  // already fits, so a control that cannot move is never offered. Same shape as
  // the Avatars tab's `hScrollWidths` (IR-1479 r3) — this is deliberately the
  // same mechanism as that tab's top bar, not a second take on it.
  const [hWidths, setHWidths] = useState({ scroll: 0, client: 0 });
  // Guards the two-way sync: assigning `scrollLeft` fires `scroll`, so without
  // this each nudge would bounce between the bars.
  const syncingRef = useRef(false);

  // IR-1228 r7 (Harel, 2026-09-14): "why the section of the list is so small".
  // The viewport lock makes height a fixed budget and this strip was spending
  // ~340px of it, leaving ~3 rows. Collapsing it hands that back to the list.
  const [statsCollapsed, setStatsCollapsed] = useState(loadStatsCollapsed);
  // IR-1228 r8: the strip also steps aside while the operator reads the list.
  // Kept separate from the button's state so scrolling back to the top restores
  // whatever they chose, rather than silently overwriting their preference.
  const [statsHiddenByScroll, setStatsHiddenByScroll] = useState(false);
  const statsHidden = statsCollapsed || statsHiddenByScroll;
  const toggleStats = () => {
    // 🪤 Flip what is ON SCREEN, not the button's own flag. The strip has two
    // independent reasons to be hidden, so toggling `statsCollapsed` alone made
    // the button lie: scrolled into the list it read "Show stats", and pressing
    // it set collapsed=true — still hidden, and now hidden for a second reason.
    const next = !statsHidden;
    setStatsCollapsed(next);
    saveStatsCollapsed(next);
    // An explicit press outranks the scroll position, which is the other half
    // of making the label true.
    setStatsHiddenByScroll(false);
  };

  // IR-1228 r7: the tab root's height, measured rather than guessed — see
  // `lockHeightFor` for why the two previous magic numbers both rotted.
  const rootRef = useRef<HTMLDivElement>(null);
  const [lockHeight, setLockHeight] = useState<number | null>(null);
  useLayoutEffect(() => {
    const el = rootRef.current;
    if (!el) return;
    const measure = () => {
      // `getBoundingClientRect().top` is viewport-relative, so it shrinks as the
      // page scrolls. Add back the scroll offset so the number means "distance
      // into the content" and the lock is the same whether or not the operator
      // happens to be scrolled when we measure.
      //
      // 🪤 Only the NEAREST real scroller counts. Summing every scrolled
      // ancestor double-counts the moment two of them are scrolled at once,
      // which inflates `top`, shrinks the lock, and leaves the band of empty
      // page this whole measurement exists to remove.
      let scrolled = 0;
      for (let n = el.parentElement; n; n = n.parentElement) {
        const oy = getComputedStyle(n).overflowY;
        if ((oy === "auto" || oy === "scroll") && n.scrollHeight > n.clientHeight) {
          scrolled = n.scrollTop;
          break;
        }
      }
      // The document itself, when nothing in between is the scroller.
      if (scrolled === 0) scrolled = window.scrollY || 0;
      const rect = el.getBoundingClientRect();
      const top = rect.top + scrolled;
      // The page's own bottom padding sits under this tab and is window space
      // the tab cannot use; read it instead of hardcoding today's 96px.
      const page = el.closest(".iris-page");
      const pad = page ? Number.parseFloat(getComputedStyle(page).paddingBottom) || 0 : 0;
      // Everything in the tab that is not the table. Derived (root minus table)
      // rather than enumerated, so it stays true when the strip collapses or
      // the filter bar wraps onto a second line. Stable under its own result:
      // growing the root grows the table by the same amount, leaving this equal.
      const card = tableScrollRef.current;
      const chrome = card ? Math.max(0, rect.height - card.clientHeight) : 0;
      setLockHeight(lockHeightFor(window.innerHeight, top, pad, chrome));
    };
    measure();

    // 🪤 MEASURING ONCE WAS THE BUG (Harel, 2026-09-14: "looks bad!"). The first
    // version ran this on mount and then only on window resize, so anything that
    // changed height afterwards — the filter bar wrapping, the strip collapsing,
    // the bulk bar appearing, the page arriving with a restored scroll position
    // — left a stale lock. A lock shorter than the space available renders as a
    // band of empty page under the tab, which is exactly what he screenshotted.
    // Observing means the number cannot go stale, whatever moves above it.
    //
    // Guarded: an environment without ResizeObserver (jsdom, a very old
    // browser) must fall back to the window-resize listener, not throw during
    // commit and take the whole tab down with it.
    let ro: ResizeObserver | null = null;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(measure);
      ro.observe(el);
      const page = el.closest(".iris-page");
      if (page) ro.observe(page);
    }
    window.addEventListener("resize", measure);
    return () => {
      ro?.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, []);

  const syncScroll = (from: "top" | "table") => () => {
    if (syncingRef.current) {
      syncingRef.current = false;
      return;
    }
    const src = from === "top" ? topScrollRef.current : tableScrollRef.current;
    const dst = from === "top" ? tableScrollRef.current : topScrollRef.current;
    if (!src || !dst || src.scrollLeft === dst.scrollLeft) return;
    syncingRef.current = true;
    dst.scrollLeft = src.scrollLeft;
  };

  // IR-1228 r8 (Harel, 2026-09-15): "the table should be full page i think."
  // Reading the list means you are no longer reading the headline, so the strip
  // steps aside and the table takes the screen. Guarded by `syncScroll`'s own
  // handler rather than a second listener so the two cannot disagree about
  // which element scrolled.
  const onListScroll = (el: HTMLDivElement | null) => {
    if (!el) return;
    setStatsHiddenByScroll((prev) => {
      const next = shouldHideStatsForScroll(el.scrollTop, prev);
      // IR-1228 r8b (Harel, 2026-09-15): the band of empty page under the
      // pagination, again — but from the other direction this time.
      //
      // On a window too short to hold the strip AND a usable list, the tab is
      // deliberately taller than the viewport (see `lockHeightFor`), so the page
      // is scrollable. Hiding the strip then shrinks the tab by ~280px while the
      // operator's scroll position stays put, so the content ends ABOVE the fold
      // and the gap shows as black. Returning to the top costs nothing: the only
      // things above the table are the title, the tabs and the strip we just
      // hid, and the table is about to occupy the screen anyway.
      if (next && !prev) scrollPageToTop(el);
      return next;
    });
  };

  // The proxy's spacer has to match the table's true width, which changes with
  // the data (a long avatar name widens a column). Measured after paint rather
  // than assumed, and re-measured when the rows change.
  const rowCount = results.length;
  useEffect(() => {
    const el = tableScrollRef.current;
    if (!el) return;
    // `rowCount` is read here, not just depended on: an empty page has nothing
    // to scroll, so the proxy bar must collapse rather than sit above the
    // "No groups found" message offering to move columns that are not there.
    const measure = () =>
      setHWidths(
        rowCount > 0
          ? { scroll: el.scrollWidth, client: el.clientWidth }
          : { scroll: 0, client: 0 },
      );
    measure();
    // ResizeObserver is absent in some jsdom setups; the initial measure above
    // is what the tests assert on, so its absence must not throw.
    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [rowCount]);

  const columns: DataTableColumn<UnifiedGroupRecord>[] = [
    // IR-1228 (Rotem c61856, 2026-09-07): the column ORDER is hers, verbatim —
    // "Checkbox / name / platform / group ID / Status - change to Lifecycle /
    // Collection / Last Monitored / Coverage / Ingestion / AvatarName / Added
    // to Avatar / Added to system". She listed it as the fix for "the data is
    // genuinely not visible in the screen cause the table is poorly built": the
    // four identity columns first, then the four status layers in pipeline
    // order, then the dates.
    //
    // Her list stops at twelve. The columns below it — Risk, Members, Messages,
    // Notes and the two action columns — are NOT dropped: she asked for Members
    // to stay ("remove the members collumns ... but for now, keep it cause I
    // dont trust the numbers", c61808) and never ruled on the others, so they
    // trail her twelve rather than disappearing on an inference.

    {
      // IR-661 Task 12: per-row + select-all-on-page checkbox for bulk
      // operations. Header reads "Sel."; cell stops propagation so the
      // checkbox click doesn't open the right-side detail panel.
      key: "__select",
      label: (
        <input
          type="checkbox"
          aria-label="Select all on page"
          // IR-1426: over the VISIBLE rows, not the raw page. A select-all that
          // silently swept in rows folded under a collapsed toggle would arm a
          // bulk delete against groups the operator cannot see.
          checked={allOnPageSelected(visibleRows)}
          onChange={(e) => {
            e.stopPropagation();
            toggleSelectAllOnPage(visibleRows);
          }}
          onClick={(e) => e.stopPropagation()}
          style={{ accentColor: "var(--iris-neon-purple)" }}
        />
      ) as unknown as string,
      render: (g) => (
        <input
          type="checkbox"
          aria-label={`Select ${g.display_name}`}
          checked={selectedIds.has(g.id)}
          onChange={(e) => {
            e.stopPropagation();
            toggleSelectId(g.id);
          }}
          onClick={(e) => e.stopPropagation()}
          style={{ accentColor: "var(--iris-neon-purple)" }}
        />
      ),
    },
    {
      key: "display_name",
      label: "Name",
      sortable: true,
      render: (g) => (
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          <GroupNameButton group={g} onOpen={() => setCardGroup(g)} />
          {isCoverageTerminated(g) && (
            <Chip
              style={{ fontSize: 9, padding: "1px 6px", color: "var(--iris-neon-pink)" }}
              title="Coverage terminated — avatar removed from this group's monitoring"
            >
              {"⊘ Coverage Terminated"}
            </Chip>
          )}
        </span>
      ),
    },
    {
      // IR-1147 reopen: Platform now sortable + rendered as a brand icon
      // (Rotem round-3: "put the platforms icons - they are cute"). Falls back
      // to the text chip for unknown platforms (PlatformIcon handles that too).
      key: "group_application_type",
      label: "Platform",
      // IR-1228 (Rotem c61856): "platform - same width as status column". One
      // constant feeds both, so they cannot drift apart in a later round.
      width: STATUS_COL_WIDTH,
      sortable: true,
      cellVariant: "mono",
      render: (g) => {
        const code = platformToIconCode(g.group_application_type);
        return code ? (
          <PlatformIcon platform={code} />
        ) : (
          <Chip style={{ fontSize: 10, padding: "2px 8px" }}>
            {g.group_application_type ?? "\u2014"}
          </Chip>
        );
      },
    },
    {
      // IR-1147 reopen: Group ID column (Rotem round-3: "Add groupID"). The
      // scrapping_id is the stable per-group scraper identifier shown in the
      // detail panel; surface it as a mono column here too.
      key: "scrapping_id",
      label: "Group ID",
      cellVariant: "mono",
      // IR-1228 r5 (Rotem, 2026-09-08 "group ID - limiy size to
      // '1951027298'", repeated 2026-09-09 "Ive asked it to be narrower").
      // Telegram ids are ~10 digits, but Signal and WhatsApp ids are base64 and
      // run to 44 characters — one of those was setting the width of the column
      // for all 25 rows and pushing Ingestion off the right-hand edge, which is
      // the same complaint as "the data is genuinely not visible".
      //
      // Sized to her example (10 mono digits + padding) and truncated with an
      // ellipsis rather than wrapped: a wrapped id would make one row twice the
      // height of its neighbours.
      width: GROUP_ID_COL_WIDTH,
      render: (g) => (
        <span
          style={{
            color: "var(--iris-fg-4)",
            fontFamily: "var(--iris-font-mono)",
            fontSize: 10,
            display: "block",
            maxWidth: GROUP_ID_COL_WIDTH,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
          title={g.scrapping_id ?? undefined}
        >
          {g.scrapping_id ?? "\u2014"}
        </span>
      ),
    },
    {
      key: "prod_group_status",
      // IR-1228 (Rotem c61856): "Stataus - change to Lifecycle". The column has
      // always shown the LIFECYCLE layer (GroupStatusCell renders
      // prod_group_status, the business state); "Status" was the one word the
      // four-layer model exists to stop using, because Collection, Coverage and
      // Ingestion beside it are statuses too.
      //
      // IR-1425 r10 (Rotem, ROUNDS 4): the column now reads the matrix LCP —
      // one of 10 words with its reason under it, Quiet as a badge — from the
      // row's `lcp*` fields (packages 1-3). `prod_group_status*` is no longer
      // rendered here but stays in the type and in the fallback: a row with
      // `lcp === undefined` (pre-port production backend) or `lcp === null`
      // (an internally deleted group) still gets today's GroupStatusCell. The
      // column `key` stays "prod_group_status" — column-visibility prefs and
      // tests key on it.
      label: "Lifecycle",
      width: STATUS_COL_WIDTH,
      render: (g) =>
        typeof g.lcp === "string" ? (
          <span style={{ display: "inline-flex", flexDirection: "column", gap: 1, minWidth: 0 }}>
            <LifecycleChip
              lcp={g.lcp}
              label={g.lcp_label}
              color={g.lcp_color}
              quiet={g.lcp_quiet}
              quietWindowDays={breakdown?.quiet_window_days ?? 7}
            />
            <LifecycleReason
              lcp={g.lcp}
              lcp_reason={g.lcp_reason}
              lcp_reason_label={g.lcp_reason_label}
              lcp_source={g.lcp_source}
              lcp_set_at={g.lcp_set_at}
              lcp_avatar_note={g.lcp_avatar_note}
            />
            <AvatarStopLines stop={g.avatar_stop} />
          </span>
        ) : (
          <GroupStatusCell
            status={g.prod_group_status}
            color={g.prod_group_status_color}
            label={g.prod_group_status_label}
            missedCycles={g.prod_missed_cycles}
            failureKind={g.prod_failure_kind}
            coveringAvatar={g.prod_covering_avatar}
            adminState={g.prod_admin_state}
          />
        ),
    },
    {
      // IR-1227 post-meeting (Rotem 55*): ONE merged collection status across
      // envs \u2014 replaces the per-env "Prod"/"Dev" collection columns that made the
      // same shared collection look like two different statuses (each env logs
      // the same scrape minutes apart \u2192 boundary straddle). "The collection is
      // one status." Per-env Deleted is surfaced only when it genuinely diverges
      // (CollectionCell). Sorts by prod entity status (closest server-side key;
      // the merged lifecycle is derived and tracks prod in ~98% of rows).
      key: "prod_status",
      label: "Collection",
      sortable: true,
      render: (g) => <CollectionCell group={g} />,
    },
    {
      // IR-1627: did the last collection read all the new messages?
      //
      // Reads like the Collection / Coverage / Ingestion family and follows the
      // same evidence rule — an unknown renders as an em dash with no dot,
      // because a collector that does not report must never be printed as
      // Complete. Telegram and WhatsApp report; Signal does not yet.
      //
      // Placed HERE, right after "Collection" (Rotem, c62949 2026-09-26,
      // translated: "after you explained it relates to COLLECTION, I wrote
      // that I understand and asked that it sit right next to the COLLECTION
      // column the figure refers to, with an explanation in a tooltip") —
      // this SUPERSEDES the c62764/c62917 "after Ingestion" placement (moved
      // there in a4d9f7d, shipped in 05c6dd6), which she called the wrong
      // anchor once she saw it live: the value is about what the collector
      // read, not about what reached the database, so it belongs beside the
      // column describing the collection run, not the ingestion pipeline.
      //
      // 🪤 Named "Messages Read", not "Messages" — "Messages (prod/dev)" below
      // is the message COUNT, and two columns both opening with "Messages"
      // would be ambiguous on screen (and made an existing header assertion
      // match the wrong column).
      key: "messages_quality",
      // Header tooltip (Rotem c62949: "with an explanation in a tooltip") —
      // Ingestion Matrix quality vocabulary (Complete / Partial / Unknown),
      // scoped to the group's last COLLECTION run, never the Ingestion axis.
      label: (
        <span title="Messages Read: what the last collection run got off the platform for this group. Complete = everything new was read. Partial = it stopped early; what it did collect was kept. Unknown = this collector does not report yet — never shown as Complete. Not applicable = collection is confirmed stopped, historic, waiting for approval or the latest result is Not Checked — the previous result stays visible under it. With several avatars in one round, the group shows the most complete result; each avatar's own result is listed in the cell.">
          Messages Read
        </span>
      ) as unknown as string,
      render: (g) => (
        <MessagesQualityCell
          quality={g.messages_quality ?? null}
          label={g.messages_quality_label ?? null}
          stopReason={g.messages_stop_reason ?? null}
          inProcess={Boolean(g.messages_in_process)}
          at={g.messages_quality_at ?? null}
          previous={g.messages_quality_previous ?? null}
          previousLabel={g.messages_quality_previous_label ?? null}
          previousAt={g.messages_quality_previous_at ?? null}
          observation={g.collection_observation ?? null}
          observationLabel={g.collection_observation_label ?? null}
          collectionAxis={g.collection_axis ?? null}
          lcp={g.lcp ?? null}
          avatars={g.messages_quality_avatars ?? null}
        />
      ),
    },
    {
      // IR-1228 r5 (Rotem, 2026-09-09): "add a collum last collection date
      // after collection", and when asked which date she meant: "It should be
      // - data received - message or member roster".
      //
      // Served as `last_data_at` — the later of the group's last message and
      // its newest member-roster stamp, folded server-side. Both halves are
      // needed: messages exist for 1,607 of 4,119 production groups, and a
      // group we are still sitting in delivers a roster every cycle even when
      // nobody posts (1,467 groups have a roster newer than their last
      // message; on 72 the last message is over a year old).
      //
      // Time as well as date, matching Last Monitoring — the two are read
      // against each other ("we checked at X, data last arrived at Y") and a
      // day-resolution answer cannot support that comparison.
      //
      // IR-1425 r12b (Rotem 2026-10-12, comment 64191): the value is now the
      // COLLECTOR's own record — `last_collection_at`, read from its report /
      // session folder — with the kind of data it delivered as a sub-line
      // (`last_collection_kind_label`: Messages / Member list / Kind not
      // reported). Shown in the viewer's local time with the zone beside it.
      //
      // 🪤 OPTIONAL FIELD. iris-ops reaches prod on its own while iris-backend
      // waits for an explicit pick, so on production this is `undefined` until
      // that lands and every row shows an em dash. That is the designed
      // degradation, not a bug — do NOT "fix" it by falling back to
      // `last_data_at` (the ingestion-side receipt) or `last_message_at`:
      // each silently answers a different question than the one the column's
      // header asks.
      key: "last_collection_at",
      label: "Last Collection",
      render: (g) => (
        <CollectorStamp
          iso={g.last_collection_at}
          kindLabel={g.last_collection_kind_label ?? undefined}
          title="The collector's own record of the last data it delivered from this group, and what kind — a message batch or a member list. Shown in your local time; the zone is printed beside it."
        />
      ),
    },
    // IR-1697 r14: Ingestion (the per-environment "Last data received ... UTC"
    // text) sits right after Last Collection, ahead of Last Monitoring and
    // Coverage, so it is on the first screen at 1440 (pre-QA F1; Mahdi 66145).
    {
      // IR-1228 P5 (L3 ingestion), upgraded under IR-1697: per-environment BY
      // NATURE -- prod and dev hold different data -- so unlike IR-1227's
      // merged Collection column these must not be folded into one word.
      // Both PROD and DEV are now visible together, in that fixed order,
      // matching the Members/Messages prod/dev pair convention -- DEV is no
      // longer tooltip-only.
      key: "prod_ingestion",
      label: "Ingestion",
      // IR-1697 r11 (QA finding 3): at 1440 the column was squeezed to one
      // letter wide. A floor on the header cell governs the whole column.
      minWidth: INGESTION_COL_MIN_WIDTH,
      render: (g) => (
        <IngestionPairCell
          prodValue={g.prod_ingestion}
          prodLabel={g.prod_ingestion_label ?? g.prod_ingestion}
          devValue={g.dev_ingestion}
          devLabel={g.dev_ingestion_label ?? g.dev_ingestion}
          prodReceivedAt={g.prod_last_ingested_at}
          prodReceiptLabel={g.prod_receipt_label}
          devReceivedAt={g.dev_last_ingested_at}
          devReceiptLabel={g.dev_receipt_label}
        />
      ),
    },
    {
      // IR-1045 P4: Last Monitoring Time (entities.last_monitoring_time). The
      // "Added to system" column above is "first monitoring"/"added" (IR-1147,
      // renamed from "Created" in r4).
      // IR-1147 reopen: full date (Rotem round-3 "display full date").
      // IR-1425 r12b (Rotem 2026-10-12): the value is the COLLECTOR's latest
      // attempt to reach the group, failed attempts included
      // (`last_monitoring_at`), in the viewer's local time with the zone.
      // Never fall back to `last_monitoring_time` — that is the ingestion
      // heartbeat, which still feeds the Ingestion column; on a backend that
      // predates r12b this cell reads an em dash by design.
      key: "last_monitoring_at",
      label: "Last Monitoring",
      sortable: true,
      // IR-1228 r5 (Rotem, twice): "last monitoing time - I want the time as
      // well!". This column answers "how fresh is this check" — at day
      // resolution every group monitored at any point today reads identically,
      // which is exactly the question it is being asked to settle.
      render: (g) => (
        <CollectorStamp
          iso={g.last_monitoring_at}
          title="The collector's latest attempt to reach this group, failed attempts included — not an ingestion stamp. Shown in your local time; the zone is printed beside it."
        />
      ),
    },
    {
      // IR-1765 r2 (Rotem 66052, 65730): the group's own latest activity time, NOT Last Monitoring (kept as the
      // separate column before it). Unknown = word + info icon, outdated date = date + warning icon; the wording
      // is in LastActiveDisplay only. Sorted server-side, Unknown last in both directions.
      key: "last_active_at",
      label: "Last Active",
      sortable: true,
      render: (g) => (
        <LastActiveDisplay
          lastActiveAt={g.last_active_at}
          outdated={Boolean(g.last_active_outdated_warning)}
        />
      ),
    },
    {
      // IR-1228 P5 (L4 coverage). Rotem's column spec c61508. The value is the
      // phone-authoritative fold from api/ops/services/group_coverage.py —
      // "is the covering avatar actually in the room". Distinct from
      // `coverage_status`, which is the IR-1045 P4b MANUAL override.
      //
      // null means the covering avatar has never reported: UNKNOWN, not "not
      // covered". It renders an em dash, because printing a negative there
      // would be a claim the data does not support — the same evidence rule
      // that makes group_coverage.is_covered return None rather than False.
      key: "coverage",
      label: "Coverage",
      render: (g) => (
        <PipelineStatusCell
          value={g.coverage}
          label={g.coverage_label ?? g.coverage}
          variants={COVERAGE_VARIANT}
        />
      ),
    },
    {
      key: "avatars",
      // IR-1228 (Rotem c61856): "AvatarName". Same cell — the chips already
      // carry each covering avatar's name — only the header says which of the
      // avatar's several identifiers it is showing.
      label: "Avatar Name",
      // IR-1147 reopen (Tomer 2026-06-30): hover reveals each connected avatar's
      // device name + mobile number (Join-Links-tab tooltip pattern).
      // IR-1426 r2 (Rotem): this column shows the ASSIGNMENT — Excel seed plus
      // the automatic matcher — so per her rule it carries the "Reported" mark,
      // fed by the row-level fold the backend already sends.
      render: (g) => <AvatarsCell avatars={g.avatars} onPhone={g.on_phone} />,
    },
    {
      // IR-1147 reopen: "Added to Avatar" \u2014 earliest AvatarGroup.assigned_at
      // (the date Rotem actually meant by "added"; distinct from "Added to
      // system" = first-seen-in-IRIS, r4). Full date; em dash when the group
      // has no avatar.
      key: "added_to_avatar",
      label: "Added to Avatar",
      render: (g) => (
        <span style={{ color: "var(--iris-fg-3)" }}>{fmtCreatedDate(g.added_to_avatar)}</span>
      ),
    },
    {
      // IR-1384 r4 (Sean: "change the button's name to added to system"). The
      // column moves with the pill — r3's rule is that the two carry ONE name,
      // and "Added to system" beside "Added to Avatar" says which date it is
      // in a way "Created" never did.
      key: "created_at",
      label: "Added to system",
      sortable: true,
      render: (g) => (
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          <span style={{ color: "var(--iris-fg-3)" }}>{fmtCreatedDate(g.created_at)}</span>
          {isNewGroup(g.created_at, Date.now()) && (
            <Chip style={{ fontSize: 9, padding: "1px 6px", color: "var(--iris-neon-cyan)" }}>
              NEW
            </Chip>
          )}
        </span>
      ),
    },
    {
      key: "risk_score",
      label: "Risk",
      sortable: true,
      headerAlign: "right",
      cellVariant: "num",
      render: (g) =>
        g.risk_score != null ? (
          <span style={{ color: riskColor(g.risk_score), fontWeight: 600 }}>{g.risk_score}</span>
        ) : (
          <span style={{ color: "var(--iris-fg-5)" }}>{"\u2014"}</span>
        ),
    },
    {
      key: "__members",
      label: "Members (prod/dev)",
      sortable: true,
      headerAlign: "right",
      cellVariant: "num",
      render: (g) => (
        <span style={{ color: "var(--iris-fg-3)" }}>{fmtPair(g.prod_members, g.dev_members)}</span>
      ),
    },
    {
      key: "__messages",
      label: "Messages (prod/dev)",
      sortable: true,
      headerAlign: "right",
      cellVariant: "num",
      render: (g) => (
        <span style={{ color: "var(--iris-fg-3)" }}>
          {fmtPair(g.prod_messages, g.dev_messages)}
        </span>
      ),
    },
    {
      key: "notes",
      label: "Notes",
      render: (g) => <NotesCell group={g} />,
    },
    {
      key: "__lifecycle_actions",
      label: "",
      render: (g: UnifiedGroupRecord) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              aria-label="Lifecycle actions"
              className="iris-btn"
              onClick={(e) => e.stopPropagation()}
              style={{ padding: "2px 6px" }}
            >
              <MoreHorizontal size={14} />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem
              onClick={() => lifecycleAction.mutate({ id: g.id, action: "approve" })}
            >
              Approve
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => lifecycleAction.mutate({ id: g.id, action: "not_relevant" })}
            >
              Mark Not Relevant
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => lifecycleAction.mutate({ id: g.id, action: "set_inactive" })}
            >
              Set Inactive
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
    {
      key: "__delete",
      label: "",
      // IR-1351's lesson, applied the moment this table gained a horizontal
      // scrollbar: the one control you must never scroll to find is Delete.
      // Pinning it to the right edge means future columns push the SCROLL, not
      // the button.
      cellClassName: STICKY_ACTIONS_CLASS,
      render: (g: UnifiedGroupRecord) => (
        <button
          type="button"
          aria-label="Delete group"
          className="iris-btn"
          onClick={(e) => {
            e.stopPropagation();
            setDeleteTarget(g);
          }}
          style={{ padding: "2px 8px", fontSize: 11 }}
        >
          Delete
        </button>
      ),
    },
  ];

  return (
    // IR-1228 r5 (Rotem, 2026-09-09): "the table header should be sticky and yet
    // im losing it and the table".
    //
    // 🔑 THE HEADER WAS ALREADY `stickyHeader`. That was never the bug. Sticky
    // pins an element to its nearest SCROLLING ANCESTOR, which here is the
    // table's own bounded box — and that box sits below a tall stats strip
    // inside a page that scrolls. So the header stuck faithfully to the top of a
    // container that was itself sliding out of the viewport, taking the header
    // with it. Her screenshot is precisely that: rows, no header, nothing above.
    //
    // The fix is to stop the PAGE being the scroller for this tab. The tab is
    // locked to the viewport, the strip and filters take their natural height,
    // and the table box claims the rest and scrolls internally — so the box
    // never leaves the screen and its sticky header has somewhere to stick.
    //
    // `minHeight: 0` on both this row and the column inside it is load-bearing,
    // not defensive: a flex item's default `min-height: auto` refuses to shrink
    // below its content, so without it the table box grows to fit all 25 rows,
    // pushes past the viewport, and the page starts scrolling again — the exact
    // bug, restored, with more CSS.
    //
    // IR-1228 r7 (Harel, 2026-09-14): the height was `calc(100vh - 130px)`, and
    // a `220` before that. Both were guesses at the furniture above this tab,
    // and both drifted. The real offset is 237px plus the page's own 96px of
    // bottom padding, so the root overshot the window by ~200px: the app shell
    // grew a scrollbar, and the bottom of the list sat below the fold. It is
    // measured now (`lockHeightFor`), so there is no constant left to rot.
    // `null` on the very first paint means "not measured yet" — fall back to
    // the old expression for that one frame rather than render at zero height.
    <div
      ref={rootRef}
      style={{
        display: "flex",
        gap: 16,
        height: lockHeight === null ? "calc(100vh - 130px)" : lockHeight,
        minHeight: 0,
      }}
    >
      {/* Main panel */}
      <div
        style={{
          flex: 1,
          minWidth: 0,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          gap: 12,
        }}
      >
        {/* IR-1425 (Rotem, 2026-08-23): the three header numbers.

            Each one is captioned, and the middle caption is doing real work:
            `total_collected` is FLEET-WIDE and does not follow this page's
            filters, because the phone roster lives in the fleet database and
            the entity filters live in the other one — there is no cheap
            intersection to make. Unlabelled, an operator who filters to one
            platform and watches two numbers drop while the third holds still
            has been handed a bug report. Labelled, it reads as what it is.
            Every figure renders "—" until the backend reaches prod. */}
        {/* IR-1482 r3 (Harel, 2026-09-09): "fix the dimensions & presprective..
            look at the the avatar page - it located better in terms of UI &
            estetics".

            🪤 WHAT WAS ACTUALLY WRONG. Every item in this strip was a FLAT
            SIBLING of one wrapping flex row — six tiles, two rings and the
            Added frame, nine children competing for one line. So the tiles ate
            the first row, the collection ring landed at the end of it, and the
            ingestion ring wrapped onto a second row beside the Added frame:
            two rings the eye is meant to compare, on different lines, at
            different heights, with a dead gap under the tiles. Sizes were never
            the problem — the NESTING was.

            The Avatars strip solves this by having exactly THREE children: a
            tile GRID, a ring, a ring. Its own comment records that this strip
            "has drawn a layout complaint from Rotem in three consecutive
            rounds", so the shape is copied rather than re-invented — three
            explicit columns that wrap identically at every width instead of an
            auto-fit that packs 4 + 2 and leaves a hole.

            `alignItems: stretch` (not flex-start) is what makes the blocks
            share one baseline top AND bottom; flex-start let each block take
            its own height and is why the row read as ragged. */}
        {/* IR-1228 r7 (Harel, 2026-09-14): the strip collapses so the list can
            have its height back. Unmounted rather than hidden with CSS — a
            `display: none` block still costs nothing in a flex column, but the
            two donuts re-render on every data poll whether or not anyone can
            see them, and an operator who collapsed the strip has said they are
            not looking at it. */}
        {!statsHidden && (
          <div
            id="groups-stats-strip"
            style={{ display: "flex", flexDirection: "column", gap: 10 }}
          >
            <div
              data-testid="groups-stats-row"
              style={{ display: "flex", flexWrap: "wrap", gap: 12, alignItems: "stretch" }}
            >
              {/* Left block: the six totals as a fixed 3x2 grid, with the Added
              frame spanning all three columns beneath them.

              🪤 The Added frame stays INSIDE this block rather than moving to a
              line of its own: Rotem ruled on IR-1400 r7 that it goes "in the
              same row as Total". A second row would be the pre-r7 layout she
              rejected, dressed up as a tidier one. */}
              <div
                style={{
                  flex: "1 1 380px",
                  display: "grid",
                  gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
                  gridAutoRows: "min-content",
                  gap: 8,
                  alignContent: "start",
                }}
              >
                {[
                  {
                    // IR-1425 r10: order 4, not 6 — the two label-matched
                    // lifecycle tiles (orders 4, 5) are gone; the "parked states"
                    // Rotem's second row carried now live in the Lifecycle strip
                    // under this grid, one tile per matrix value, in her order.
                    order: 4,
                    label: "Total groups",
                    value: tilesAreFilterFree
                      ? unfiltered.total_groups
                      : (current?.summary?.total_groups ?? (current ? total : null)),
                    caption: tilesAreFilterFree
                      ? "every group, excluding deleted"
                      : "matching the current filters",
                    title: tilesAreFilterFree
                      ? "Every group we hold, apart from the deleted ones. This number does NOT follow the filters below — the count of groups matching those is on the line under these tiles."
                      : "Every group this page's filters match.",
                    color: "var(--iris-neon-cyan)",
                    testId: "summary-total-groups",
                  },
                  {
                    order: 3,
                    label: "Total collected groups",
                    value: data?.summary?.total_collected,
                    caption: "on phone · fleet-wide",
                    title:
                      "Distinct groups the handsets are carrying right now, across the whole fleet. This number does NOT follow the filters above — the phone rosters live in the fleet database and the group filters live in the IRIS one, so it stays fleet-wide rather than being narrowed to a population it cannot see.",
                    color: "var(--iris-neon-green)",
                    testId: "summary-total-collected",
                  },
                  // IR-1425 r10 (Rotem, ROUNDS 4): Active = MESSAGES ONLY. The
                  // matrix says a roster alone is not Active (r12c: it is Suspended
                  // — no messages since membership was confirmed — or Needs Review
                  // awaiting its first message), so the headline value is
                  // `active_messages_only`. Every count prints its window — the
                  // caption is derived from the served window, never typed.
                  //
                  // IR-1555 (Rotem, 2026-10-25: "we had this in OPS … write it in
                  // the same tile Active — Messages — roster only"): the roster-only
                  // figure comes back INSIDE the tile, as a second line under
                  // Messages. It is shown beside Active, never added into it — the
                  // headline stays messages-only. Messages repeats the headline so
                  // the two lines read as one pair; Roster only is the server's
                  // `active_windows[window].roster_only` (a member list in the
                  // window and no message in it), "—" when the backend has no
                  // roster source (`active_windows` dropped).
                  //
                  // Absent `active_messages_only` (pre-port production backend):
                  // today's value AND today's caption, exactly as before — the
                  // IR-1400 r7 degrade ("tiles keep the summary figures") and the
                  // IR-1425 header numbers still hold; the window caption is never
                  // printed over a figure it does not describe.
                  {
                    order: 2,
                    label: "Active groups",
                    value: activeMessagesOnly
                      ? activeMessagesOnly.days_7
                      : (breakdown?.active_windows?.days_7.total ??
                        (tilesAreFilterFree
                          ? unfiltered.active_groups
                          : current?.summary?.active_groups)),
                    caption: activeMessagesOnly
                      ? `messages · ${windowCaption(breakdown?.active_messages_only_window?.days_7.hours ?? 168)}`
                      : "active in the last week",
                    split: activeMessagesOnly
                      ? {
                          testId: "active-tile-split-7d",
                          messages: activeMessagesOnly.days_7,
                          rosterOnly: breakdown?.active_windows?.days_7.roster_only ?? null,
                        }
                      : null,
                    title: activeMessagesOnly
                      ? ACTIVE_SPLIT_TITLE
                      : "Groups with a message or member roster received in the last 7 days. Excludes deleted and Historic groups; does not follow table filters.",
                    color: "var(--iris-neon-purple)",
                    testId: "summary-active-groups",
                    quietLine: true,
                  },
                  // IR-1482 r2 (Rotem, IR-1228 2026-09-07 23:01): "add: Active
                  // grops last 24 hours". Deliberately the SAME purple as the tile
                  // above it — one metric, two windows — with the window carried by
                  // the caption, which is the only thing that differs between them.
                  // A separate hue would read as a separate measure.
                  {
                    order: 1,
                    label: "Active groups",
                    value: activeMessagesOnly
                      ? activeMessagesOnly.hours_24
                      : (breakdown?.active_windows?.hours_24.total ??
                        breakdown?.active_24h ??
                        null),
                    caption: activeMessagesOnly
                      ? `messages · ${windowCaption(breakdown?.active_messages_only_window?.hours_24.hours ?? 24)}`
                      : "active in the last 24h",
                    split: activeMessagesOnly
                      ? {
                          testId: "active-tile-split-24h",
                          messages: activeMessagesOnly.hours_24,
                          rosterOnly: breakdown?.active_windows?.hours_24.roster_only ?? null,
                        }
                      : null,
                    title: activeMessagesOnly
                      ? ACTIVE_SPLIT_TITLE
                      : "Groups with a message or member roster received in the last 24 hours. Excludes deleted and Historic groups; does not follow table filters.",
                    color: "var(--iris-neon-purple)",
                    testId: "summary-active-groups-24h",
                  },
                ]
                  // IR-1228 r5 (Rotem, 2026-09-08 and again 2026-09-09: "you
                  // ignored my comment on the order of the tiles! it has meaning"):
                  //
                  //   upper row  — Active groups 24 hours | Active groups 7 | Total collected
                  //   second row — Collection Pending     | Collection stopped | Total groups
                  //
                  // Her ordering is a reading order, not a preference: the top row
                  // is what is happening NOW (widening from 24h to 7d to ever), and
                  // the second is the states a group can be parked in, ending on the
                  // total they sum into. r3 rearranged the strip's LAYOUT and left
                  // the order alone, which is why this came back.
                  //
                  // Sorted by an explicit `order` rather than by rewriting the array
                  // literal: the entries carry conditionals and a spread, so source
                  // position is a fragile place to encode something she has now had
                  // to ask for twice. The grid is 3 columns, so index 0-2 are the
                  // upper row and 3-5 the second.
                  .sort((a, b) => a.order - b.order)
                  .map((s) => (
                    <div
                      // IR-1482 r2: keyed on testId, not label. The 24h tile is
                      // deliberately labelled "Active groups" like the 7-day one — one
                      // metric, two windows — which made the label a duplicate key and
                      // let React drop a tile.
                      key={s.testId}
                      data-testid={s.testId}
                      title={s.title}
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 1,
                        padding: "5px 12px",
                        // IR-1482 r3: the grid cell sets the width now. A 132px floor
                        // inside `minmax(0, 1fr)` columns would let a narrow viewport
                        // push the tiles past the block instead of letting them shrink
                        // — the tiles carry short counts, so there is room to give.
                        minWidth: 0,
                        border: "1px solid var(--iris-line)",
                        borderLeft: `2px solid ${s.color}`,
                        borderRadius: "var(--iris-r-md)",
                        background: "var(--iris-bg-2)",
                      }}
                    >
                      <span
                        style={{
                          fontSize: 9.5,
                          textTransform: "uppercase",
                          letterSpacing: 0.5,
                          color: "var(--iris-fg-4)",
                        }}
                      >
                        {s.label}
                      </span>
                      <span
                        style={{ fontSize: 17, fontWeight: 700, lineHeight: 1.1, color: s.color }}
                      >
                        {formatSummaryNumber(s.value)}
                      </span>
                      <span style={{ fontSize: 9.5, color: "var(--iris-fg-4)" }}>{s.caption}</span>
                      {"split" in s && s.split && (
                        <ActiveSplit
                          testId={s.split.testId}
                          messages={s.split.messages}
                          rosterOnly={s.split.rosterOnly}
                        />
                      )}
                      {"quietLine" in s && s.quietLine && (
                        <span
                          data-testid="summary-quiet-count"
                          style={{ fontSize: 10.5, color: "var(--iris-fg-3)", marginTop: 2 }}
                        >
                          {breakdown?.quiet_count != null
                            ? `Quiet ${breakdown.quiet_count.toLocaleString()} · no message for ${breakdown.quiet_window_days ?? 7}+ d, still Active`
                            : "\u2014"}
                        </span>
                      )}
                    </div>
                  ))}
                {/* IR-1147: new-groups stats (by system_creation_time).
              Compact stat strip — the dashboard KpiTile hero card (160px, heavy
              glow) is too large for this table toolbar and looked squeezed.
              IR-1431: renamed New -> Added, + the "n collecting" caption and the
              hover explanation of what the date actually means.

              IR-1400 r7 (Rotem, 2026-08-24): *"'Added groups' — frame under this
              name, place in the same row as Total"*. Three unlabelled tiles on
              their own line read as three more totals; the group needs a name to
              say what the three windows have in common, and it belongs beside
              the other figures rather than under them. So the strip moves INSIDE
              the totals row as a named frame — one row of numbers, one of them
              explicitly a breakdown.

              🪤 It stays AFTER the totals in source order on purpose:
              `groups-unified-tab.ir1431-added-tiles.test.tsx` slices this file
              from "addedTiles.map" to the Filter bar comment and asserts nothing
              in that slice uses a native `title=` attribute. Hoisting this block
              above the totals would pull their `title=` tiles into the slice and
              fail a test that is guarding something real (IR-1431 r2: native
              titles do not appear where this app has taught users to hover). */}
                <div
                  data-testid="added-groups-frame"
                  style={{
                    // IR-1482 r3: spans the tile grid it now lives in, so the frame
                    // lines up with the tiles above rather than floating beside them.
                    gridColumn: "1 / -1",
                    display: "flex",
                    flexDirection: "column",
                    gap: 4,
                    padding: "4px 10px 6px",
                    border: "1px solid var(--iris-line)",
                    borderRadius: "var(--iris-r-md)",
                    background: "var(--iris-bg-2)",
                  }}
                >
                  <span
                    style={{
                      fontSize: 9.5,
                      textTransform: "uppercase",
                      letterSpacing: 0.5,
                      color: "var(--iris-fg-4)",
                    }}
                  >
                    Added groups
                  </span>
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    {addedTiles.map((s) => {
                      const caption = addedTileCaption(s);
                      return (
                        // IR-1431 r2 (Harel, 2026-08-25): "hover on them doesnt display
                        // tooltip". The first cut used a native `title` attribute, which
                        // this app does not rely on anywhere a user is expected to find
                        // an explanation. Use the design system's Radix tooltip — the
                        // root TooltipProvider in App.tsx (delayDuration 100) is what
                        // makes it appear promptly. The WHOLE tile is the trigger, not
                        // just the icon, so hovering anywhere on it works.
                        <Tooltip key={s.key}>
                          <TooltipTrigger asChild>
                            <div
                              style={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 1,
                                padding: "5px 12px",
                                minWidth: 84,
                                border: "1px solid var(--iris-line)",
                                borderLeft: `2px solid ${s.color}`,
                                borderRadius: "var(--iris-r-md)",
                                background: "var(--iris-bg-2)",
                                cursor: "help",
                              }}
                            >
                              <span
                                style={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: 4,
                                  fontSize: 9.5,
                                  textTransform: "uppercase",
                                  letterSpacing: 0.5,
                                  color: "var(--iris-fg-4)",
                                }}
                              >
                                {s.label}
                                {/* Visible affordance — a hover-only explanation nobody
                          knows to hover for is not an explanation. */}
                                <Info size={10} aria-hidden style={{ opacity: 0.7 }} />
                              </span>
                              <span
                                style={{
                                  fontSize: 17,
                                  fontWeight: 700,
                                  lineHeight: 1.1,
                                  color: s.color,
                                }}
                              >
                                {s.value.toLocaleString()}
                              </span>
                              {caption ? (
                                <span style={{ fontSize: 9.5, color: "var(--iris-fg-4)" }}>
                                  {caption}
                                </span>
                              ) : null}
                            </div>
                          </TooltipTrigger>
                          <TooltipContent side="bottom" style={{ maxWidth: 300 }}>
                            {ADDED_TILE_TOOLTIP}
                          </TooltipContent>
                        </Tooltip>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* IR-1482 (Rotem c61604/c61616): "a design like in Avatrs tab —
              Total for LCP and than two other detailed graphs", i.e. her
              original ask of "LCP to be presented in total / Connected groups
              by collection / Connected groups by ingestion".

              Same card component as the Avatars rings, not a copy — that file's
              own comment warns a second copy lets one ring drift in size or
              empty wording, and a third would be worse.

              The counts come from the backend `breakdown`, which aggregates the
              WHOLE population. They cannot be derived from the rows: ingestion
              and the collection lifecycle are folded per row inside the page
              loop, so a ring built from `results` would describe 25 groups
              while looking like it described the fleet. */}
              {/* IR-1482 r2 (Rotem, IR-1228 2026-09-07 23:01): "fix the graphs to
              be connected" — restating her earlier "I want 'Connected by
              collection', connected by ingestion - NOT ALL".

              r1 put `lcp_total` in BOTH centres, so each ring's headline number
              was every group on the tab and the two rings read identically.
              The Avatars tab she asked us to copy puts `connectedTotal` there.
              The centres are two different numbers on purpose: this ticket's
              own "Care needed" says collection-connected and
              ingestion-connected are different populations and will differ —
              one shared figure would recreate the ambiguity IR-1228 exists to
              remove. `?? lcp_total` keeps an r1 backend rendering something
              sane, since iris-ops reaches prod ahead of iris-backend. */}
              {/* IR-1425 r10 (Rotem, ROUNDS 4): the ring is the matrix Collection
              axis — the 8 states from `by_collection_axis`, label AND colour
              from each item (a closure over the payload, never a label table:
              G14), centre = the Collecting count. No time window: this is the
              collector's current state. Against a backend that has not served
              the axis the ring says so instead of drawing `by_collection`. */}
              <ConnectedDonut
                testId="group-kpi-collection"
                title="Collection"
                hint="Every non-deleted group, split by the matrix Collection axis — the collector's current state for the group. No time window. The centre is the Collecting count. Same population as the Lifecycle tiles."
                data={collectionAxisSlices}
                colorFor={collectionAxisColor}
                total={breakdown?.by_collection_axis_total ?? 0}
                centerValue={collectionAxisCenter}
                totalLabel="Collecting"
                empty={collectionAxisEmpty}
                partialEmpty={collectionAxisEmpty}
              />
              {/* IR-1697 r11 (QA finding 1; Rotem 64438: "The summary will be separate
              for PROD and for DEV"): the one unlabelled "Connected, by ingestion"
              ring is replaced by a PROD ring and a DEV ring from
              `breakdown.by_ingestion_env`, the same titles and colours as the
              Avatars tab. Each charts the Connected groups; a deployment that
              cannot read PROD serves one Could Not Check slice carrying the
              count, never a zero. */}
              {(["prod", "dev"] as const).map((env) => {
                const served = data?.breakdown?.by_ingestion_env !== undefined;
                const label = env === "prod" ? "Production" : "Development";
                const items = envIngestionRing(data?.breakdown, env);
                return (
                  <ConnectedDonut
                    key={env}
                    testId={`group-kpi-ingestion-${env}`}
                    title={`Connected, by ingestion (${label})`}
                    hint={`The Connected groups, split by what ${label} alone says about each one. Could Not Check means this deployment cannot read ${label}; it is not No data and not zero. Not Applicable and Unknown are listed apart. The centre is the Connected count.`}
                    data={items}
                    colorFor={envIngestionSliceColor}
                    total={items.reduce((n, i) => n + i.count, 0)}
                    totalLabel="Connected"
                    empty={
                      served
                        ? (noAnswerYet ?? "No groups to chart.")
                        : (noAnswerYet ?? "Per-environment ingestion not served by this backend.")
                    }
                    partialEmpty="Nothing to chart for these groups."
                  />
                );
              })}
            </div>
            {/* IR-1425 r10 (Rotem, ROUNDS 4): one tile per matrix Lifecycle
              value, from `breakdown.by_lcp` — label and colour from the item,
              payload order (= matrix order), Historic included. Its own row
              under the headline tiles: 11 compact tiles wrap; the 3-column grid
              above is for the four headline figures and the Added frame. Same
              component as the Monitoring header, same backend function, so the
              two surfaces are equal by construction. */}
            <LcpTotals
              items={breakdown?.by_lcp}
              total={breakdown?.by_lcp_total}
              quiet={breakdown?.quiet_count}
              quietWindowDays={breakdown?.quiet_window_days}
              caption="every non-deleted group · Historic included · no window"
            />
          </div>
        )}

        {/* Filter bar */}
        <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8 }}>
          <div style={{ position: "relative" }}>
            <Search
              size={12}
              style={{
                position: "absolute",
                left: 10,
                top: "50%",
                transform: "translateY(-50%)",
                color: "var(--iris-fg-5)",
              }}
            />
            <input
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                resetPage();
              }}
              placeholder="Search groups..."
              className="iris-input"
              style={{ paddingLeft: 28, width: 220 }}
            />
          </div>

          <MultiSelectFilter
            label="Platform"
            options={PLATFORM_OPTIONS.map((p) => ({ value: p, label: p }))}
            selected={platformFilter}
            onChange={(next) => {
              setPlatformFilter(next);
              resetPage();
            }}
          />

          {/* IR-1425 r10 (Rotem, ROUNDS 4): "filters are named for the column
              they filter". Lifecycle filters the Lifecycle column (the 10
              matrix values, `lcp=`); Collection filters the Collection column
              (the 8 states, `collection_axis=`). Option labels follow the
              served breakdown so a backend relabel shows up here too. */}
          <MultiSelectFilter
            label="Lifecycle"
            options={lcpOptions(data?.breakdown?.by_lcp)}
            selected={lcpFilter}
            onChange={(next) => {
              setLcpFilter(next);
              resetPage();
            }}
          />

          <MultiSelectFilter
            label="Collection"
            options={caxisOptions(data?.breakdown?.by_collection_axis)}
            selected={collectionAxisFilter}
            onChange={(next) => {
              setCollectionAxisFilter(next);
              resetPage();
            }}
          />

          {/* IR-1697 r11 (QA finding 1): "Make ingestion filters ... explicitly
              environment-specific, in both tabs." Two filters, sent server-side as
              `prod_ingestion=` / `dev_ingestion=`; same names as the Avatars tab.
              Drawn once the backend serves `by_ingestion_env` (the same release as
              the params, so an older backend would otherwise ignore them and show
              every row), or while a choice is active so it can still be cleared. */}
          {(data?.breakdown?.by_ingestion_env !== undefined || prodIngestionFilter.length > 0) && (
            <MultiSelectFilter
              label="Ingestion PROD"
              options={ENV_INGESTION_FILTER_OPTIONS}
              selected={prodIngestionFilter}
              onChange={(next) => {
                setProdIngestionFilter(next);
                resetPage();
              }}
            />
          )}
          {(data?.breakdown?.by_ingestion_env !== undefined || devIngestionFilter.length > 0) && (
            <MultiSelectFilter
              label="Ingestion DEV"
              options={ENV_INGESTION_FILTER_OPTIONS}
              selected={devIngestionFilter}
              onChange={(next) => {
                setDevIngestionFilter(next);
                resetPage();
              }}
            />
          )}

          {/* IR-1228 P5: Historic is hidden by default (Rotem c61508). It is a
              deliberate default, not a hidden one — the control says so and
              says how many are behind it, because a screen that silently drops
              half its rows is worse than one that shows them. */}
          <button
            type="button"
            aria-pressed={showHistoric}
            onClick={() => {
              // IR-1425 r10 (decision 2): one source of truth. The toggle adds /
              // removes `historic` in the Lifecycle selection (when a selection
              // is active — an active `lcp=` would otherwise exclude it) AND
              // sends include_historic=1 (covers the empty-selection case).
              const next = !showHistoric;
              setShowHistoric(next);
              setLcpFilter((sel) => {
                if (sel.length === 0) return sel;
                const without = sel.filter((k) => k !== "historic");
                return next ? [...without, "historic"] : without;
              });
              resetPage();
            }}
            style={{
              fontSize: 11,
              padding: "4px 10px",
              borderRadius: 999,
              cursor: "pointer",
              border: "1px solid var(--iris-border-1, #30363d)",
              background: showHistoric ? "var(--iris-bg-3, #21262d)" : "transparent",
              color: showHistoric ? "var(--iris-fg-2)" : "var(--iris-fg-4)",
            }}
            title={
              showHistoric
                ? "Historic groups are included. They are legacy/manual imports, tagged out of the operational counts."
                : lcpFilter.length > 0 && !lcpFilter.includes("historic")
                  ? "Historic groups are hidden. Historic is a Lifecycle value — it is also selectable in the Lifecycle filter."
                  : "Historic groups are hidden. These are legacy/manual imports; on production they are about half of all groups."
            }
          >
            {showHistoric ? "Hiding nothing · Historic shown" : "Show Historic"}
          </button>

          {/* IR-1384 (Sean): quick windows AND a custom calendar range in the
              one control. Both sides filter the same entity date, so the former
              second pill is gone — having the range live under a different name
              is exactly why it was reported missing.
              r3 (Rotem, "why we no longer have created filter?"): the survivor
              is named for the COLUMN it filters, and "Added to Avatar" is a
              different column entirely — labelling this one bare "Added"
              pointed it at data it does not touch.
              r4 (Sean, "change the button's name to added to system"): that
              rule is kept, the name it resolves to is not. Pill AND column are
              now "Added to system" — one name for entities.system_creation_time,
              distinct from "Added to Avatar" by more than a missing suffix. */}
          <DateWindowFilter
            label="Added to system"
            options={[
              { value: "24h", label: "Last 24h" },
              { value: "7d", label: "Last 7d" },
              { value: "30d", label: "Last 30d" },
            ]}
            value={added}
            onChange={(next) => {
              setAdded(next);
              resetPage();
            }}
          />

          {/* IR-1147 reopen: per-column Avatar filter (Rotem round-2 "filters
              per column - like join link tab: Avatar"). Searchable since the
              avatar roster is large. */}
          <SearchableMultiSelectFilter
            label="Avatar"
            searchable
            options={avatarOptions}
            selected={avatarFilter}
            onApply={(next) => {
              setAvatarFilter(next);
              resetPage();
            }}
          />

          {/* IR-1426 r2 (Rotem 2026-08-24): "add filter 'Reported' / 'Excel'".
              Drawn ONLY when the endpoint says it honours the param — iris-ops
              reaches prod ahead of iris-backend, and a filter rendered against
              a backend that ignores it returns the unfiltered page and reads as
              a filter that matched everything. It appears by itself once the
              backend pick lands, with no second frontend deploy.

              THREE buckets, not her two: "Never reported" is every WhatsApp
              group until IR-1433 is fixed, and folding those into "Not
              reported" would accuse the whole WhatsApp estate of being stale
              Excel rows on an observation nobody has made. */}
          {supportsOnPhoneFilter(data?.summary) && (
            <MultiSelectFilter
              label="Reported"
              options={ON_PHONE_FILTER_OPTIONS.map((o) => ({ ...o }))}
              selected={onPhoneFilter}
              onChange={(next) => {
                setOnPhoneFilter(next);
                resetPage();
              }}
            />
          )}

          {/* IR-1627 (Rotem c63023): "add a filter on the message-collection
              status so I can filter... otherwise it's hard for me to check all
              the states." Same capability-gated, server-side shape as Reported
              above. Unknown is offered like any other bucket — it is most of
              the fleet today, and folding it away would be exactly the kind of
              collapse the Ingestion Matrix forbids. */}
          {supportsMessagesQualityFilter(data?.summary) && (
            <MultiSelectFilter
              label="Messages Read"
              options={MESSAGES_QUALITY_FILTER_OPTIONS.map((o) => ({ ...o }))}
              selected={messagesQualityFilter}
              onChange={(next) => {
                setMessagesQualityFilter(next);
                resetPage();
              }}
            />
          )}

          {/* IR-1400 r7 (Rotem, 2026-08-24): "add an indication for count".
              This span is that indication and it already existed — it sits at
              the end of the filter bar and has always reported the filtered
              total. A NEW line above the filter bar was the first cut, and
              Harel's screenshot of the deployed screen killed it: the count is
              also rendered by the pager below ("Showing 1-50 of 4,048"), so a
              third copy would have put the same number on screen three times.

              Same lesson as IR-1384, where Sean reported a missing date range
              that had shipped two months earlier under the pill beside it: when
              a user asks for something the screen already has, the fix is
              DISCOVERABILITY, not another control. Her 08-24 screenshot is
              cropped at the Avatar chip, so this count was simply off the edge
              of what she was looking at.

              So it stays the one count, and gains the second number only when
              the tiles above it stop following the filters — otherwise the
              tiles and this span would both report the filtered figure and the
              "of N" would be answering a question nothing else on screen
              asked. */}
          <span className="iris-meta" data-testid="summary-matching" style={{ marginLeft: "auto" }}>
            {isLoading
              ? "Loading..."
              : loadFailed
                ? "Couldn't load"
                : tilesAreFilterFree
                  ? `${matching.toLocaleString()} of ${unfiltered.total_groups.toLocaleString()} groups`
                  : `${total.toLocaleString()} groups`}
          </span>
          {/* IR-1228 r7: the control that gives the list its height back. It
              rides at the end of the filter bar because the bar is the one row
              that is always on screen — putting it above the strip would mean
              the button scrolled away with the thing it collapses.

              A real <button> with `aria-expanded`, not a clickable div: the
              label changes with the state, so assistive tech and the visible
              text agree, and it is keyboard-reachable like every other control
              in this row. */}
          <button
            type="button"
            className="iris-chip"
            data-testid="groups-stats-toggle"
            onClick={toggleStats}
            aria-expanded={!statsHidden}
            aria-controls="groups-stats-strip"
            title={
              statsHidden
                ? "Show the statistics strip"
                : "Hide the statistics strip to see more rows"
            }
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 4,
              cursor: "pointer",
              // 32px tall like the chips beside it, so the bar keeps one baseline.
              minHeight: 32,
            }}
          >
            {statsHidden ? <ChevronDown size={12} /> : <ChevronUp size={12} />}
            {statsHidden ? "Show stats" : "Hide stats"}
          </button>
        </div>

        {/* IR-661 Task 12: bulk-action bar appears when any rows are
            selected. Mirrors the entities-tab pattern. Bulk delete
            fans out the dual-env cascade (Task 9) per row. */}
        {selectedIds.size > 0 && (
          <BulkBar
            selectedCount={selectedIds.size}
            selectedIds={[...selectedIds]}
            selectedNames={results.filter((r) => selectedIds.has(r.id)).map((r) => r.display_name)}
            type="groups"
            onClear={() => setSelectedIds(new Set())}
            onJobEnqueued={addJobEntries}
            rescrapeIds={results
              .filter((r) => selectedIds.has(r.id))
              .map((r) => r.scrapping_id)
              .filter((s): s is string => !!s)}
          />
        )}

        {/* Active delete jobs banner */}
        <ActiveDeleteJobsBanner entries={activeJobs} onRemove={removeJob} />
        {/* IR-1726 r3: a failed load says so, next to the table it emptied —
            same banner as the Avatars tab's (IR-1412). Rows are never borrowed
            from an older filter, so the table is empty; the tiles and rings keep
            the last numbers that loaded, because they ignore the filters. */}
        {loadFailed && (
          <div
            role="alert"
            data-testid="groups-load-error"
            className="iris-card"
            style={{ padding: "10px 12px", display: "flex", alignItems: "center", gap: 12 }}
          >
            <span>
              Couldn't load the groups for these filters.{" "}
              {data
                ? "The tiles and charts above show the last numbers that loaded."
                : "The numbers above are not current."}
            </span>
            <button type="button" className="iris-btn" onClick={() => refetch()}>
              Retry
            </button>
          </div>
        )}

        {/* Table — IR-1227 (Rotem 53972): bounded scroll region so the
            DataTable's sticky header has somewhere to stick (overflowY:auto
            still clips the card's rounded corners, replacing overflow:hidden).

            IR-1228 (Rotem c61856): "the data is genuinely not visibly in the
            screen cause the table is poorly built and aso missing the
            horizontal scroll — add scroll". This container bounded the vertical
            axis only, so every column past the card's right edge was CLIPPED
            with no way to reach it — the same amputation IR-1351 fixed on the
            Avatars tab. Ingestion was the first casualty on her screenshot.

            Both axes scroll in ONE scrollport deliberately: the sticky header
            (top:0) and the pinned Delete column (right:0) each need this same
            element to be their containing scroller, and splitting the axes
            across two wrappers would leave one of them pinned to nothing.

            IR-1228 r5 (Rotem, 2026-09-09): "the table header should be sticky
            and yet im losing it and the table". The bounded region above was
            necessary but not sufficient — see the tab-root comment: the box
            itself was leaving the viewport, so its sticky header left with it.
            The height now comes from the flex parent rather than from a `100vh`
            guess, which is also what retires a magic 220 that no longer matched
            the strip above it. The one-scrollport rule is unchanged. */}
        {/* The mirrored horizontal scrollbar (Rotem: "place it in the table
            header"). It is a SEPARATE element but NOT a split of the axes — the
            real scrollport below still owns both, so the sticky header and the
            pinned Delete column keep their containing scroller. This one only
            reflects and drives `scrollLeft`, and is hidden from assistive tech
            because it duplicates a control that already exists. */}
        <div
          className="iris-scroll-x iris-hscroll-top"
          data-testid="groups-table-scroll-top"
          ref={topScrollRef}
          onScroll={syncScroll("top")}
          aria-hidden="true"
          // Rendered always and hidden with `display`, matching the Avatars tab:
          // an element that comes and goes as the data changes is harder to
          // assert on and makes the strip above it jump by 12px on every page.
          style={{
            display: hWidths.scroll > hWidths.client ? "block" : "none",
            flexShrink: 0,
          }}
        >
          <div style={{ width: hWidths.scroll, height: 1 }} />
        </div>
        <div
          className="iris-card iris-scroll-x"
          data-testid="groups-table-scroll"
          ref={tableScrollRef}
          onScroll={(e) => {
            syncScroll("table")();
            onListScroll(e.currentTarget);
          }}
          style={{
            padding: 0,
            // IR-1228 r5: the flex parent sets the height now. `flex: 1` claims
            // whatever the strip and filters leave, and `minHeight: 0` is what
            // lets it actually shrink to that — a flex item defaults to
            // `min-height: auto` and would otherwise grow to fit all 25 rows,
            // push past the viewport, and restore the page scroll that broke
            // the sticky header in the first place.
            flex: 1,
            minHeight: 0,
            // IR-1228 r7 (Harel, 2026-09-14): "there is vertical scroll that
            // allows to scroll out of the screen".
            //
            // 🔑 `overflow` only clips a descendant whose CONTAINING BLOCK is
            // inside this element. Every row renders an absolutely-positioned
            // `.sr-only` span (the DataTable's screen-reader labels), and this
            // card was `position: static` — so their containing block resolved
            // to the nearest positioned ancestor, the page wrapper's
            // `.relative`. They escaped this scrollport entirely and were laid
            // out at their full un-scrolled offsets, stretching the app shell's
            // scrollHeight to ~2,500px against an ~830px viewport. That ~1,500px
            // of empty page below the content is what Harel scrolled into.
            //
            // Measured on production before/after: shell scrollHeight
            // 2,513 -> 1,010. Purely structural — nothing moves on screen.
            position: "relative",
            overflowY: "auto",
            overflowX: "auto",
          }}
        >
          {isLoading ? (
            <div style={{ padding: "16px 18px", color: "var(--iris-fg-3)", fontSize: 12 }}>
              Loading...
            </div>
          ) : (
            <DataTable
              className="iris-table--wide"
              columns={columns}
              data={visibleRows}
              rowKey={(g) => g.id}
              onSort={handleSort}
              sortKey={sortKey}
              sortDirection={sortDirection}
              emptyMessage={
                loadFailed ? "Couldn't load the groups — use Retry above." : "No groups found"
              }
              stickyHeader
            />
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginTop: 6,
              fontSize: 12,
              color: "var(--iris-fg-4)",
            }}
          >
            <span>
              Showing {(page - 1) * PAGE_SIZE + 1}&ndash;{Math.min(page * PAGE_SIZE, total)} of{" "}
              {total.toLocaleString()}
            </span>
            <div style={{ display: "flex", gap: 8 }}>
              <button
                type="button"
                className="iris-btn"
                disabled={page <= 1}
                onClick={() => setPage((p) => p - 1)}
              >
                Previous
              </button>
              <button
                type="button"
                className="iris-btn iris-btn--primary"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Side panel */}
      {selected && (
        <div className="iris-card" style={{ width: 280, flexShrink: 0, overflow: "hidden" }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              borderBottom: "1px solid var(--iris-line-1)",
              padding: "12px 16px",
            }}
          >
            <span style={{ fontSize: 12, fontWeight: 600, color: "var(--iris-fg-1)" }}>
              Group Detail
            </span>
            <button
              type="button"
              onClick={() => setSelected(null)}
              style={{
                color: "var(--iris-fg-4)",
                background: "transparent",
                border: "none",
                cursor: "pointer",
              }}
            >
              {"\u2715"}
            </button>
          </div>
          <GroupDetailPanel
            group={selected}
            onDeleteRequested={() => setDeleteTarget(selected)}
            onEditRequested={() => setEditTarget(selected)}
            onRemoveRequested={() => handleRemoveGroup(selected)}
            removePending={terminateCoverage.isPending}
          />
        </div>
      )}

      {/* IR-1228 P5: the group card. Its own slot, not nested in another
          conditional — it opens from the name and is independent of the
          delete/edit dialogs. */}
      <GroupCard group={cardGroup} open={cardGroup !== null} onClose={() => setCardGroup(null)} />

      {deleteTarget ? (
        <GroupDeleteDialog
          group={deleteTarget}
          onClose={() => setDeleteTarget(null)}
          onJobEnqueued={onJobEnqueued}
        />
      ) : null}

      {editTarget ? (
        <GroupEditDialog group={editTarget} onClose={() => setEditTarget(null)} />
      ) : null}
    </div>
  );
}

// Kept importable from here: tests and the drill-down reach the cell by this name.
export { IngestionPairCell, PipelineStatusCell };
